/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useOutletContext
} from "react-router-dom";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { evidenciasDeInteraccion, EvidenciasHallazgo, Sidebar, TablaFiltrable } from "../../../shared";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Image } from "primereact/image";
import {
  Boton,
  Titulo,
  Formulario,
  CampoInformacion,
  ArchivoPreview,
  subirArchivosPesadosS3,
  SubidorArchivos,
  accessAPI,
  formatFecha,
  downloadFile,
  useIsMobile,
  usePermiso
} from "../../../shared";
import FlujoActividad, { decisionesDePaso } from "./FlujoActividad";
import { Skeleton } from "primereact/skeleton";
import AnalisisRiesgo from "./AnalisisRiesgo";
import AnalisisIA from "./AnalisisIA";
import { useContextoAsistente } from "../../asistente/ContextoAsistente";

const FRECUENCIA_LABEL = {
  SEMANAL: "Semanal",
  MENSUAL: "Mensual",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual",
  BIENAL: "Bienal",
  TRIENAL: "Trienal"
};

// Tipo de paso "Ensayo" (catálogo fijo de la API, utils/pasoTipo.js)
const PASO_TIPO_ENSAYO = 5;

export default function ProcesoActividad() {
  const { proceso, loading: procesoLoading } = useOutletContext();
  const [loaderActividades, setLoaderActividades] = useState(true);
  const loader = procesoLoading || loaderActividades;
  const [actividades, setActividades] = useState([]);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [sidebarAgregar, setSidebarAgregar] = useState(false);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // Estado para el sidebar del paso
  const [pasoSeleccionado, setPasoSeleccionado] = useState(null);
  const [sidebarPaso, setSidebarPaso] = useState(false);
  // Guarda el id del paso cuyo formulario está enviando, para que en el
  // sidebar de todos los pasos solo cargue el formulario que corresponde
  const [loaderColaborador, setLoaderColaborador] = useState(null);
  const [formKeyColaborador, setFormKeyColaborador] = useState(0);

  // Sidebar de asignación de colaboradores de todos los pasos
  const [sidebarColaboradores, setSidebarColaboradores] = useState(false);
  const [loaderDecisionAdmin, setLoaderDecisionAdmin] = useState(false);
  const [formKeyDecisionAdmin, setFormKeyDecisionAdmin] = useState(0);

  // Estado para el Análisis de IA (por actividad; solo lectura en admin)
  const [analisisIA, setAnalisisIA] = useState(null);
  const [sidebarAnalisisIA, setSidebarAnalisisIA] = useState(false);
  const [analizandoIA, setAnalizandoIA] = useState(false);
  const [sidebarPromptIA, setSidebarPromptIA] = useState(false);
  const [formPromptKey, setFormPromptKey] = useState(0);
  // Interval del polling del análisis (en ref para poder cancelarlo).
  const intervaloIARef = useRef(null);
  // Timeout del polling: pasado este tiempo se cancela la corrida por tiempo.
  const TIMEOUT_ANALISIS_MS = 5 * 60 * 1000;

  // Estado para planificaciones
  const [planificaciones, setPlanificaciones] = useState([]);
  const [sidebarPlanificacion, setSidebarPlanificacion] = useState(false);
  const [loaderPlanificacion, setLoaderPlanificacion] = useState(false);
  const [formKeyPlanificacion, setFormKeyPlanificacion] = useState(0);
  const [planificacionMenu, setPlanificacionMenu] = useState(null);
  const [planificacionEditando, setPlanificacionEditando] = useState(null);

  // Facturas emitidas del proceso (accordion "Facturación"). null = cargando.
  const [facturasProceso, setFacturasProceso] = useState(null);

  // Estado para el menú contextual y la edición de actividades
  const [actividadMenu, setActividadMenu] = useState(null);
  const [sidebarEditarActividad, setSidebarEditarActividad] = useState(false);
  const [loaderEditarActividad, setLoaderEditarActividad] = useState(false);
  const [formKeyEditarActividad, setFormKeyEditarActividad] = useState(0);

  // Estado para detalle de revisión en sidebar del paso
  const [productosRevision, setProductosRevision] = useState(null);
  const [loaderRevision, setLoaderRevision] = useState(false);
  const [imagenesVerificaciones, setImagenesVerificaciones] = useState({});

  // Estado para dialog de comentarios

  // Estado para menú de archivos de actividad
  const [archivoMenuSeleccionado, setArchivoMenuSeleccionado] = useState(null);
  const [descargandoArchivoId, setDescargandoArchivoId] = useState(null);
  const menuArchivo = useRef(null);

  // Sidebar de subida de archivos de la actividad: el archivo se sube con una
  // categoría elegida de las configuradas por el organismo, o una custom
  // escrita a mano (que pisa a la del dropdown)
  const [sidebarSubirArchivo, setSidebarSubirArchivo] = useState(false);
  const [categoriasArchivos, setCategoriasArchivos] = useState([]);
  const [formLoaderSubirArchivo, setFormLoaderSubirArchivo] = useState(false);
  const [formKeySubirArchivo, setFormKeySubirArchivo] = useState(0);

  // Sidebar de edición de categoría de un archivo ya subido
  const [sidebarCategoriaArchivo, setSidebarCategoriaArchivo] = useState(false);
  const [formLoaderCategoriaArchivo, setFormLoaderCategoriaArchivo] =
    useState(false);
  const [formKeyCategoriaArchivo, setFormKeyCategoriaArchivo] = useState(0);

  // Carga todas las categorías de archivos del organismo
  useEffect(() => {
    accessAPI(
      "GET",
      "admin/config/categoriasarchivos?dropdown=true",
      null,
      (respuesta) => {
        setCategoriasArchivos(respuesta);
      },
      () => {}
    );
  }, []);

  // Estado para hallazgos
  const [hallazgoTipos, setHallazgoTipos] = useState([]);
  const [hallazgoSeleccionado, setHallazgoSeleccionado] = useState(null);
  const [sidebarHallazgoDetalle, setSidebarHallazgoDetalle] = useState(false);
  const [sidebarHallazgoEditar, setSidebarHallazgoEditar] = useState(false);
  const [sidebarHallazgoInteraccion, setSidebarHallazgoInteraccion] = useState(false);
  const [loaderHallazgoEditar, setLoaderHallazgoEditar] = useState(false);
  const [loaderHallazgoInteraccion, setLoaderHallazgoInteraccion] = useState(false);
  const menuHallazgoRef = useRef(null);

  const toast = useRef(null);
  const menuColaboradorTodos = useRef(null);
  const menuPlanificacion = useRef(null);
  const menuActividad = useRef(null);
  const isMobile = useIsMobile();
  const [asignacionMenu, setAsignacionMenu] = useState(null);
  const { puedeVer: puedeVerFacturacion } = usePermiso("admin.facturacion");
  const [loaderEnsayo, setLoaderEnsayo] = useState(false);
  // Paso al que se le está asignando o editando el ensayo (abre su sidebar)
  const [pasoEnsayo, setPasoEnsayo] = useState(null);
  // Paso al que pertenece la asignación cuyo kebab está abierto
  const [pasoMenu, setPasoMenu] = useState(null);
  const { procesoId, actividadId } = useParams();
  useContextoAsistente({
    tipo: "actividad",
    id: actividadSeleccionada?.id,
    nombre: actividadSeleccionada?.actividadtipo?.nombre
  });
  const navigate = useNavigate();
  // El override por ?volver lo maneja el componente Titulo; acá solo el default.
  const urlVolver = proceso?.cliente?.id
    ? `/clientes/${proceso.cliente.id}/procesos`
    : `/proceso/${procesoId}`;

  // Inicializa planificaciones cuando el proceso está disponible
  useEffect(() => {
    if (proceso) {
      setPlanificaciones(proceso.planificacion || []);
    }
  }, [proceso]);

  // Cargar actividades cuando el proceso esté listo
  useEffect(() => {
    if (proceso) {
      accessAPI(
        "GET",
        `admin/actividad/proceso/${procesoId}`,
        null,
        (response) => {
          setActividades(response);
          // Si hay un actividadId en la URL, seleccionar esa actividad
          if (actividadId) {
            const actividad = response.find(
              (a) => a.id === parseInt(actividadId)
            );
            if (actividad) {
              setActividadSeleccionada(actividad);
            }
          }
          setLoaderActividades(false);
        },
        (error) => {
          setLoaderActividades(false);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: error[0]?.msg || "Error al cargar actividades",
            sticky: true
          });
        }
      );
    }
  }, [procesoId, proceso, actividadId]);

  // Carga las facturas emitidas del proceso (son por proceso, no por
  // actividad, así que se cargan una sola vez).
  useEffect(() => {
    // Sin permiso de facturación no se consultan
    if (!puedeVerFacturacion) return;
    accessAPI(
      "GET",
      `admin/facturacion/proceso/${procesoId}/facturas`,
      null,
      (response) => setFacturasProceso(response),
      () => setFacturasProceso([])
    );
  }, [procesoId, puedeVerFacturacion]);

  // Carga el Análisis de IA de la actividad seleccionada (si existe)
  useEffect(() => {
    if (actividadSeleccionada?.id) {
      accessAPI(
        "GET",
        `admin/proceso/actividad/${actividadSeleccionada.id}/analisis`,
        null,
        (response) => setAnalisisIA(response),
        () => setAnalisisIA(null)
      );
    } else {
      setAnalisisIA(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actividadSeleccionada?.id]);

  // Dispara el análisis con IA para la actividad seleccionada y hace polling.
  function analizarConIA(promptCustom) {
    const actividadId = actividadSeleccionada?.id;
    if (!actividadId) return;
    setAnalizandoIA(true);
    accessAPI(
      "POST",
      `admin/proceso/actividad/${actividadId}/analizar`,
      promptCustom ? { promptCustom } : {},
      () => {
        const inicio = Date.now();
        if (intervaloIARef.current) clearInterval(intervaloIARef.current);
        intervaloIARef.current = setInterval(() => {
          accessAPI(
            "GET",
            `admin/proceso/actividad/${actividadId}/analisis`,
            null,
            (r) => {
              if (r && r.estado && r.estado !== "procesando") {
                clearInterval(intervaloIARef.current);
                intervaloIARef.current = null;
                setAnalizandoIA(false);
                setAnalisisIA(r);
                if (r.estado === "error") {
                  toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: "El análisis falló: " + (r.error || ""),
                    sticky: true
                  });
                } else {
                  toast.current?.show({
                    severity: "success",
                    summary: "Listo",
                    detail: "Análisis completado",
                    life: 3000
                  });
                }
              } else if (Date.now() - inicio > TIMEOUT_ANALISIS_MS) {
                // Se pasó del tiempo máximo: cancela la corrida por tiempo.
                cancelarAnalisisIA("tiempo");
              }
            },
            () => {}
          );
        }, 5000);
      },
      (r) => {
        setAnalizandoIA(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo iniciar el análisis",
          sticky: true
        });
      }
    );
  }

  // Cancela el análisis en curso (por decisión del usuario o por tiempo de
  // espera): frena el polling y marca la corrida vigente como error para que no
  // quede colgada en "procesando" si el asistente nunca reportó.
  function cancelarAnalisisIA(motivo, actividadIdParam) {
    const actividadId = actividadIdParam || actividadSeleccionada?.id;
    if (intervaloIARef.current) {
      clearInterval(intervaloIARef.current);
      intervaloIARef.current = null;
    }
    setAnalizandoIA(false);
    // Limpia el loader local si seguimos viendo esa misma actividad.
    if (actividadSeleccionada?.id === actividadId) {
      setAnalisisIA((prev) =>
        prev && prev.estado === "procesando"
          ? { ...prev, estado: "error", error: "Cancelado" }
          : prev
      );
    }
    if (!actividadId) return;
    accessAPI(
      "PUT",
      `admin/proceso/actividad/${actividadId}/analisis/cancelar`,
      { motivo },
      () => {
        toast.current?.show({
          severity: "info",
          summary: "Cancelado",
          detail:
            motivo === "tiempo"
              ? "El análisis se canceló por tiempo de espera."
              : "Análisis cancelado.",
          life: 4000
        });
      },
      () => {}
    );
  }

  // Limpia el interval del polling al desmontar.
  useEffect(() => {
    return () => {
      if (intervaloIARef.current) clearInterval(intervaloIARef.current);
    };
  }, []);

  function abrirPromptSidebar() {
    setFormPromptKey((k) => k + 1);
    setSidebarPromptIA(true);
  }

  // Formulario del sidebar "Analizar con prompt": un textarea con el prompt
  // destacado que se envía al asistente para este análisis particular.
  const formConfigPromptIA = {
    secciones: [
      {
        campos: [
          {
            nombre: "prompt",
            tipoitemformularioid: 11,
            etiqueta: "Prompt destacado para este análisis",
            requerido: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Analizar",
        clases: "verde",
        validar: true,
        callback: (campos) => {
          setSidebarPromptIA(false);
          analizarConIA(campos.prompt);
        }
      }
    ]
  };

  // Cargar tipos de hallazgo al montar
  useEffect(() => {
    accessAPI("GET", "admin/hallazgo/tipodropdown", null, (respuesta) => {
      setHallazgoTipos(respuesta);
    });
  }, []);

  // Helper para actualizar un hallazgo en el state de actividades y actividadSeleccionada
  function actualizarHallazgoEnState(hallazgoActualizado) {
    setActividades((prev) =>
      prev.map((a) =>
        a.id === actividadSeleccionada.id
          ? {
              ...a,
              hallazgos: a.hallazgos.map((h) =>
                h.id === hallazgoActualizado.id ? hallazgoActualizado : h
              )
            }
          : a
      )
    );
    setActividadSeleccionada((prev) => ({
      ...prev,
      hallazgos: prev.hallazgos.map((h) =>
        h.id === hallazgoActualizado.id ? hallazgoActualizado : h
      )
    }));
  }

  // Editar hallazgo
  function editarHallazgo(contenidoCampos) {
    setLoaderHallazgoEditar(true);
    accessAPI(
      "PUT",
      `admin/hallazgo/${hallazgoSeleccionado.id}`,
      {
        hallazgoTipoId: contenidoCampos.hallazgoTipoId,
        resumen: contenidoCampos.resumen,
        texto: contenidoCampos.texto
      },
      (respuesta) => {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Hallazgo actualizado correctamente",
          life: 3000
        });
        actualizarHallazgoEnState(respuesta);
        setLoaderHallazgoEditar(false);
        setSidebarHallazgoEditar(false);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error, intente nuevamente más tarde.",
          sticky: true
        });
        setLoaderHallazgoEditar(false);
      }
    );
  }

  // Enviar interacción de admin
  function enviarInteraccionAdmin(contenidoCampos, archivo) {
    setLoaderHallazgoInteraccion(true);
    accessAPI(
      "PUT",
      `admin/hallazgo/${hallazgoSeleccionado.id}/interaccion`,
      { contenido: contenidoCampos.contenido },
      (respuesta) => {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Comentario enviado correctamente",
          life: 3000
        });
        actualizarHallazgoEnState(respuesta);
        setHallazgoSeleccionado(respuesta);
        setLoaderHallazgoInteraccion(false);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error, intente nuevamente más tarde.",
          sticky: true
        });
        setLoaderHallazgoInteraccion(false);
      },
      archivo
    );
  }

  // Cerrar hallazgo
  function cerrarHallazgoAdmin(contenidoCampos, archivo) {
    setLoaderHallazgoInteraccion(true);
    accessAPI(
      "PUT",
      `admin/hallazgo/${hallazgoSeleccionado.id}/cerrar`,
      { contenido: contenidoCampos.contenido },
      (respuesta) => {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Hallazgo cerrado correctamente",
          life: 3000
        });
        actualizarHallazgoEnState(respuesta);
        setLoaderHallazgoInteraccion(false);
        setSidebarHallazgoInteraccion(false);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error, intente nuevamente más tarde.",
          sticky: true
        });
        setLoaderHallazgoInteraccion(false);
      },
      archivo
    );
  }

  // Menú contextual del hallazgo (igual que en la plataforma de
  // colaboradores): cerrado, solo ver sus detalles; abierto, la interacción y,
  // mientras nadie haya interactuado (el cliente todavía no respondió),
  // editarlo
  const menuHallazgoItems = useMemo(() => {
    if (!hallazgoSeleccionado) return [];
    if (hallazgoSeleccionado.fechaCierre) {
      return [
        {
          label: "Ver detalles",
          icon: "pi pi-eye",
          command: () => setSidebarHallazgoDetalle(true)
        }
      ];
    }
    const items = [];
    if (!hallazgoSeleccionado.interacciones?.length) {
      items.push({
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => setSidebarHallazgoEditar(true)
      });
    }
    items.push({
      label: "Interacción",
      icon: "pi pi-comments",
      command: () => setSidebarHallazgoInteraccion(true)
    });
    return items;
  }, [hallazgoSeleccionado]);

  // Body para columna de acciones en DataTable de hallazgos
  function accionesHallazgo(rowData) {
    return (
      <Boton
        texto
        negro
        callback={(e) => {
          setHallazgoSeleccionado(rowData);
          menuHallazgoRef.current.toggle(e);
        }}
      >
        <i className="pi pi-ellipsis-v"></i>
      </Boton>
    );
  }

  // Render helper para lista de interacciones de un hallazgo
  function renderInteracciones(hallazgo, rutaBase) {
    if (!hallazgo.interacciones?.length) return null;
    return hallazgo.interacciones.map((interaccion, idx) => (
      <div
        key={idx}
        css={{
          padding: "0.75em",
          marginBottom: "0.5em",
          borderRadius: "6px",
          backgroundColor: interaccion.cierre ? "#f0fdf4" : "#f8f9fa",
          border: interaccion.cierre
            ? "1px solid #16a34a"
            : "1px solid #dee2e6"
        }}
      >
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5em"
          }}
        >
          <span
            css={{
              fontWeight: "bold",
              fontSize: "0.9em",
              display: "flex",
              alignItems: "center",
              gap: "0.4em"
            }}
          >
            {interaccion.cierre && (
              <i
                className="pi pi-check-circle"
                css={{ color: "#16a34a" }}
              ></i>
            )}
            {interaccion.usuario}
          </span>
          <div
            css={{
              display: "flex",
              gap: "0.5em",
              alignItems: "center"
            }}
          >
            {interaccion.colaborador && (
              <Tag
                value="Colaborador"
                severity="info"
                css={{ fontSize: "0.7em" }}
              />
            )}
            {interaccion.cliente && (
              <Tag
                value="Cliente"
                severity="success"
                css={{ fontSize: "0.7em" }}
              />
            )}
            {interaccion.admin && (
              <Tag
                value="Admin"
                severity="warning"
                css={{ fontSize: "0.7em" }}
              />
            )}
            <span css={{ fontSize: "0.8em", color: "#6c757d" }}>
              {formatFecha(interaccion.fecha)}
            </span>
          </div>
        </div>
        {interaccion.textos?.map((texto, tIdx) => (
          <div key={tIdx} css={{ marginBottom: "0.25em" }}>
            <span
              css={{
                fontSize: "0.8em",
                color: "#6c757d",
                fontWeight: "600"
              }}
            >
              {texto.nombre}:
            </span>
            <p
              css={{
                margin: "0.2em 0 0 0",
                fontSize: "0.9em",
                whiteSpace: "pre-wrap"
              }}
            >
              {texto.contenido}
            </p>
          </div>
        ))}
        {evidenciasDeInteraccion(interaccion).length > 0 && (
          <EvidenciasHallazgo
            rutaBase={rutaBase}
            hallazgoId={hallazgo.id}
            evidencias={evidenciasDeInteraccion(interaccion)}
            idsEnActividad={(actividadSeleccionada?.archivos || []).map((a) => a.id ?? a.archivoId)}
            puedeMover
            onMovido={(archivo) => handleArchivosActividadSubidos([archivo])}
            toast={toast}
          />
        )}
      </div>
    ));
  }

  // Template para el tipo de actividad
  const tipoTemplate = (rowData) => {
    return <span>{rowData.actividadtipo?.nombre}</span>;
  };


  // Template para la fecha de creación
  const fechaTemplate = (rowData) => {
    return <span>{formatFecha(rowData.fechacreacion)}</span>;
  };

  // Template para el punto de estado
  const estadoTemplate = (rowData) => {
    // Verde si está finalizada; rojo si terminó sin cumplimiento
    if (rowData.fechafinalizacion) {
      return (
        <span
          css={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: rowData.finalizadaSinCumplimiento
              ? "var(--rojo)"
              : "var(--verde)"
          }}
        />
      );
    }

    // Con pasos en paralelo puede haber más de un paso actual
    const pasosActuales = (rowData.actividadpaso || []).filter(
      (paso) => paso.actual
    );
    const pasoInicialId = rowData.flujo?.pasoInicialId;

    // Amarillo si ya avanzó del primer paso
    if (
      pasosActuales.some((paso) =>
        pasoInicialId
          ? paso.id !== pasoInicialId
          : paso.orden > 1
      )
    ) {
      return (
        <span
          css={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: "var(--amarillo)"
          }}
        />
      );
    }

    // Sin punto si está en el primer paso
    return null;
  };

  // Template para el ícono de comentario (planificación y/o status del paso)
  const comentarioTemplate = (rowData) => {
    const comentarioPlanificacion = rowData.comentarioplanificacion;
    // Un comentario de status por cada paso actual (más de uno si hay pasos
    // en paralelo)
    const comentariosStatus = (rowData.actividadpaso || [])
      .filter((p) => p.actual && p.comentarioStatus)
      .map((p) => p.comentarioStatus);

    if (!comentarioPlanificacion && !comentariosStatus.length) return null;

    const partes = [];
    if (comentarioPlanificacion) partes.push(comentarioPlanificacion);
    partes.push(...comentariosStatus);
    const textoTooltip = partes.join("\n\n");

    const tooltipId = `comentario-${rowData.id}`;
    return (
      <>
        <Tooltip target={`#${tooltipId}`} position="top">
          <span css={{ whiteSpace: "pre-line" }}>{textoTooltip}</span>
        </Tooltip>
        <i
          id={tooltipId}
          className="pi pi-info-circle"
          style={{
            color: "var(--amarillo)",
            fontSize: "1.2rem",
            cursor: "pointer"
          }}
        />
      </>
    );
  };

  // Función para agregar una actividad
  function handleAgregarActividad(camposFormulario) {
    setLoaderAgregar(true);
    accessAPI(
      "POST",
      `admin/actividad/actividad/${proceso.id}`,
      camposFormulario,
      (respuesta) => {
        // Recargar actividades para obtener los datos completos con pasos
        accessAPI(
          "GET",
          `admin/actividad/proceso/${procesoId}`,
          null,
          (actividadesActualizadas) => {
            setActividades(actividadesActualizadas);
            // Seleccionar la actividad recién creada
            const nuevaActividad = actividadesActualizadas.find(
              (a) => a.id === respuesta.id
            );
            if (nuevaActividad) {
              setActividadSeleccionada(nuevaActividad);
            }
            setSidebarAgregar(false);
            setFormKey((prev) => prev + 1);
            setLoaderAgregar(false);
            // Si la API facturó la actividad, avisa que también se creó la
            // planificación de facturación
            const facturacion = respuesta.facturacion;
            toast.current?.show({
              severity: "success",
              summary: "Éxito",
              detail: facturacion
                ? `Actividad creada correctamente. Se creó también una planificación de facturación por ${
                    facturacion.moneda?.simbolo || ""
                  } ${facturacion.precio}.`
                : "Actividad creada correctamente"
            });
          },
          (error) => {
            setLoaderAgregar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: error[0]?.msg || "Error al recargar actividades",
              sticky: true
            });
          }
        );
      },
      (error) => {
        setLoaderAgregar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al crear actividad",
          sticky: true
        });
      }
    );
  }

  // Configuración del formulario para agregar actividad
  const formConfigAgregar = proceso
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "actividadtipoid",
                tipoitemformularioid: 5,
                etiqueta: "Tipo de actividad",
                requerido: true,
                opciones: `admin/actividad/actividadTipo?dropdown=true&certificadotipo=${proceso.certificadotipo.id}`,
                sinOpcionInicial: true
              },
              {
                nombre: "comentarioplanificacion",
                tipoitemformularioid: 11,
                etiqueta: "Comentarios"
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Crear actividad",
            clases: "verde",
            callback: handleAgregarActividad,
            validar: true
          }
        ]
      }
    : null;

  // Handler para seleccionar una fila
  const onRowSelect = (e) => {
    setActividadSeleccionada(e.data);
    // Actualizar la URL sin navegar
    window.history.replaceState(
      null,
      "",
      `/proceso/${procesoId}/actividades/${e.data.id}`
    );
  };

  // Una actividad sólo se puede eliminar si nunca se trabajó: sin colaboradores
  // asignados en los pasos, sin comentarios, sin decisiones de auditor ni admin,
  // sin archivos ni hallazgos. (El backend revalida estas condiciones.)
  const actividadEsEliminable = (actividad) => {
    if (!actividad) return false;
    const pasos = actividad.actividadpaso || [];
    const tieneColaborador = pasos.some(
      (p) => (p.actividadpaso_x_auditor || []).length > 0
    );
    const tieneComentarioPaso = pasos.some((p) => p.comentarioStatus);
    const tieneDecisionAuditor = pasos.some((p) =>
      (p.actividadpaso_x_auditor || []).some(
        (a) => (a.actividadpasodecision || []).length > 0
      )
    );
    const tieneDecisionAdmin = pasos.some(
      (p) => (p.actividadpasodecisionadmin || []).length > 0
    );
    const tieneHallazgos = (actividad.hallazgos || []).length > 0;
    const tieneArchivos = (actividad.archivos || []).length > 0;
    return (
      !tieneColaborador &&
      !tieneComentarioPaso &&
      !tieneDecisionAuditor &&
      !tieneDecisionAdmin &&
      !tieneHallazgos &&
      !tieneArchivos
    );
  };

  // Handler para eliminar una actividad que nunca se trabajó
  const handleEliminarActividad = (actividad) => {
    if (!actividad) return;
    const actividadId = actividad.id;
    accessAPI(
      "DELETE",
      `admin/actividad/${actividadId}`,
      null,
      (respuesta) => {
        setActividades((prev) => prev.filter((a) => a.id !== actividadId));
        // Si era la actividad abierta en el panel derecho, se limpia
        if (actividadSeleccionada?.id === actividadId) {
          setActividadSeleccionada(null);
          window.history.replaceState(
            null,
            "",
            `/proceso/${procesoId}/actividades`
          );
        }
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Actividad eliminada"
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar actividad",
          sticky: true
        });
      }
    );
  };

  // Handler para editar la fecha de una actividad desde el menú contextual
  function handleEditarActividad(camposFormulario) {
    if (!actividadMenu) return;
    const actividadId = actividadMenu.id;
    const camposEditados = {
      fechacreacion: camposFormulario.fecha,
      comentarioplanificacion: camposFormulario.comentarioplanificacion || null
    };
    setLoaderEditarActividad(true);
    accessAPI(
      "PUT",
      `admin/actividad/${actividadId}`,
      camposFormulario,
      (respuesta) => {
        setActividades((prev) =>
          prev.map((a) =>
            a.id === actividadId ? { ...a, ...camposEditados } : a
          )
        );
        setActividadSeleccionada((prev) =>
          prev?.id === actividadId ? { ...prev, ...camposEditados } : prev
        );
        setSidebarEditarActividad(false);
        setLoaderEditarActividad(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta[0]?.msg || "Actividad actualizada"
        });
      },
      (error) => {
        setLoaderEditarActividad(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al editar actividad",
          sticky: true
        });
      }
    );
  }

  // Handler para abrir el sidebar del paso
  const handleClickPaso = (paso, timelineItem) => {
    setPasoSeleccionado(paso);
    setSidebarPaso(true);
    setProductosRevision(null);

    // Si el paso tuvo alguna decisión, cargar el detalle de revisión
    if (timelineItem?.decision) {
      setLoaderRevision(true);
      accessAPI(
        "GET",
        `admin/actividad/paso/${paso.id}/productos`,
        null,
        (respuesta) => {
          setProductosRevision(respuesta);
          setLoaderRevision(false);
        },
        () => {
          setLoaderRevision(false);
        }
      );
    }
  };

  // Función para cargar imagen de verificación
  const cargarImagenVerificacion = useCallback(
    (verificacionId) => {
      if (imagenesVerificaciones[verificacionId]) return;
      accessAPI(
        "GET",
        `admin/actividad/comentariocampo/${verificacionId}/archivo`,
        null,
        (blob) => {
          const url = URL.createObjectURL(blob);
          setImagenesVerificaciones((prev) => ({
            ...prev,
            [verificacionId]: url
          }));
        },
        () => {
          setImagenesVerificaciones((prev) => ({
            ...prev,
            [verificacionId]: "error"
          }));
        }
      );
    },
    [imagenesVerificaciones]
  );

  // Función para agregar colaborador a un paso
  function handleAgregarColaborador(camposFormulario, pasoDestino) {
    if (!pasoDestino) return;

    setLoaderColaborador(pasoDestino.id);
    accessAPI(
      "PUT",
      `admin/actividad/paso/${pasoDestino.id}/auditor`,
      camposFormulario,
      (respuesta) => {
        // Actualizar el paso en la actividad seleccionada
        setActividades((prev) =>
          prev.map((act) => {
            if (act.id !== actividadSeleccionada.id) return act;
            return {
              ...act,
              actividadpaso: act.actividadpaso.map((paso) => {
                if (paso.id !== pasoDestino.id) return paso;
                return {
                  ...paso,
                  actividadpaso_x_auditor: [
                    ...(paso.actividadpaso_x_auditor || []),
                    respuesta
                  ]
                };
              })
            };
          })
        );

        // Actualizar también la actividad seleccionada
        setActividadSeleccionada((prev) => ({
          ...prev,
          actividadpaso: prev.actividadpaso.map((paso) => {
            if (paso.id !== pasoDestino.id) return paso;
            return {
              ...paso,
              actividadpaso_x_auditor: [
                ...(paso.actividadpaso_x_auditor || []),
                respuesta
              ]
            };
          })
        }));

        // Actualizar el paso seleccionado si es el mismo
        setPasoSeleccionado((prev) =>
          prev && prev.id === pasoDestino.id
            ? {
                ...prev,
                actividadpaso_x_auditor: [
                  ...(prev.actividadpaso_x_auditor || []),
                  respuesta
                ]
              }
            : prev
        );

        setFormKeyColaborador((prev) => prev + 1);
        setLoaderColaborador(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Colaborador agregado correctamente"
        });
      },
      (error) => {
        setLoaderColaborador(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al agregar colaborador",
          sticky: true
        });
      }
    );
  }

  // Vuelve a pedir las actividades y refresca la actividad y el paso abiertos
  function recargarActividades() {
    accessAPI(
      "GET",
      `admin/actividad/proceso/${procesoId}`,
      null,
      (actividadesActualizadas) => {
        setActividades(actividadesActualizadas);
        const actActualizada = actividadesActualizadas.find(
          (a) => a.id === actividadSeleccionada.id
        );
        if (actActualizada) {
          setActividadSeleccionada(actActualizada);
          setPasoSeleccionado((prev) => {
            if (!prev) return prev;
            return (
              actActualizada.actividadpaso.find((p) => p.id === prev.id) || prev
            );
          });
        }
      },
      () => {}
    );
  }

  // Asocia (o desasocia, si viene vacío) un ensayo al paso del sidebar de
  // ensayo
  function handleGuardarEnsayo(campos) {
    setLoaderEnsayo(true);
    accessAPI(
      "PUT",
      `admin/actividad/paso/${pasoEnsayo.id}/ensayo`,
      { ensayoId: campos.ensayoId || null },
      () => {
        setLoaderEnsayo(false);
        setPasoEnsayo(null);
        recargarActividades();
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Número de ensayo guardado"
        });
      },
      (error) => {
        setLoaderEnsayo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al guardar el número de ensayo",
          sticky: true
        });
      }
    );
  }

  // Ensayo de un paso de tipo Ensayo, para su caja del flujograma: el número
  // (que abre la muestra) con un botón de editar, o el botón para asignarlo.
  function bloqueEnsayo(paso) {
    if (paso?.pasoTipo?.id !== PASO_TIPO_ENSAYO) {
      return null;
    }
    return (
      // Estos clicks no tienen que abrir el sidebar del paso
      <div
        onClick={(e) => e.stopPropagation()}
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          "& .p-button": { padding: "0.2em 0.4em" }
        }}
      >
        {/* Sin número (sin asignar o gestionado externamente) va el botón */}
        {paso.ensayo ? (
          <>
            <small className="text-500">
              <strong>Ensayo:</strong>
            </small>
            {paso.ensayo.muestraid ? (
              // El volver de la muestra regresa a esta actividad
              <Boton
                texto
                chico
                uri={`/muestra/${paso.ensayo.muestraid}?volver=/proceso/${procesoId}/actividades/${actividadSeleccionada.id}`}
              >
                {String(paso.ensayo.numero)}
              </Boton>
            ) : (
              <small>{paso.ensayo.numero}</small>
            )}
            <Boton icono negro callback={() => setPasoEnsayo(paso)}>
              <i className="pi pi-pencil" />
            </Boton>
          </>
        ) : (
          <Boton texto chico callback={() => setPasoEnsayo(paso)}>
            Asignar ensayo
          </Boton>
        )}
      </div>
    );
  }

  // Asigna un administrador a un paso (solo en los pasos que lo aceptan)
  function handleAgregarAdministrador(usuarioId, pasoDestino) {
    if (!pasoDestino) return;

    setLoaderColaborador(pasoDestino.id);
    accessAPI(
      "PUT",
      `admin/actividad/paso/${pasoDestino.id}/administrador`,
      { usuarioId },
      () => {
        recargarActividades();
        setFormKeyColaborador((prev) => prev + 1);
        setLoaderColaborador(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Administrador agregado correctamente"
        });
      },
      (error) => {
        setLoaderColaborador(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al agregar administrador",
          sticky: true
        });
      }
    );
  }

  // Quita a un administrador de un paso
  function handleEliminarAdministrador(asignacionId, pasoDestino) {
    accessAPI(
      "DELETE",
      `admin/actividad/paso/${pasoDestino.id}/administrador/${asignacionId}`,
      null,
      (respuesta) => {
        recargarActividades();
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar administrador",
          sticky: true
        });
      }
    );
  }

  // Función para eliminar colaborador de un paso
  function handleEliminarColaborador(asignacionId, pasoDestino) {
    if (!pasoDestino) return;

    accessAPI(
      "DELETE",
      `admin/actividad/paso/${pasoDestino.id}/auditor/${asignacionId}`,
      null,
      (respuesta) => {
        // Recargar actividades para obtener el estado actualizado
        accessAPI(
          "GET",
          `admin/actividad/proceso/${procesoId}`,
          null,
          (actividadesActualizadas) => {
            setActividades(actividadesActualizadas);
            // Actualizar la actividad seleccionada
            const actActualizada = actividadesActualizadas.find(
              (a) => a.id === actividadSeleccionada.id
            );
            if (actActualizada) {
              setActividadSeleccionada(actActualizada);
              // Actualizar el paso seleccionado si sigue abierto
              setPasoSeleccionado((prev) => {
                if (!prev) return prev;
                return (
                  actActualizada.actividadpaso.find((p) => p.id === prev.id) ||
                  prev
                );
              });
            }
          },
          () => {}
        );

        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar colaborador",
          sticky: true
        });
      }
    );
  }

  // Configuración del formulario para agregar colaborador a un paso
  const generarFormConfigColaborador = (pasoDestino) => {
    // Construir URL con queryparams para filtrar auditores formados
    let auditorDropdownUrl = "admin/auditor/dropdown";
    if (proceso && pasoDestino) {
      const normaId = proceso.norma?.id;
      const esquemaId = proceso.esquema?.id;
      const auditorTipoId =
        pasoDestino.decisorTipoId;

      if (normaId && esquemaId && auditorTipoId) {
        auditorDropdownUrl = `admin/auditor/dropdown?norma=${normaId}&esquema=${esquemaId}&auditortipo=${auditorTipoId}`;
      }
    }
    // Si el paso lo acepta, el dropdown lista también a los administradores
    // asignables. Vienen con value "admin-<id de usuario>".
    if (pasoDestino?.aceptaAdministrador) {
      auditorDropdownUrl += `${
        auditorDropdownUrl.includes("?") ? "&" : "?"
      }administradores=true`;
    }

    return {
      secciones: [
        {
          nombre: pasoDestino?.nombre,
          campos: [
            {
              nombre: "auditorid",
              tipoitemformularioid: 5,
              etiqueta: "Agregar colaborador",
              requerido: true,
              opciones: auditorDropdownUrl,
              sinOpcionInicial: true
            },
            {
              nombre: "decisor",
              tipoitemformularioid: 6,
              etiqueta: "Decisor",
              requerido: false
            },
            {
              nombre: "enformacion",
              tipoitemformularioid: 6,
              etiqueta: "En formación",
              requerido: false
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Agregar",
          clases: "verde",
          callback: (campos) => {
            // Un administrador no es decisor ni está en formación: va por su
            // propio endpoint.
            if (String(campos.auditorid).startsWith("admin-")) {
              handleAgregarAdministrador(
                parseInt(String(campos.auditorid).replace("admin-", "")),
                pasoDestino
              );
              return;
            }
            const datosParaEnviar = {
              auditorid: campos.auditorid,
              decisor: campos.decisor === true,
              enformacion: campos.enformacion === true
            };
            handleAgregarColaborador(datosParaEnviar, pasoDestino);
          },
          validar: true
        }
      ]
    };
  };

  // Modelo del menú kebab de una asignación de colaborador. Lo comparten el
  // sidebar del paso y el de colaboradores de todos los pasos.
  const modeloMenuColaborador = () => [
    {
      label: "Ver colaborador",
      icon: "pi pi-user",
      command: (e) => {
        if (asignacionMenu?.auditor?.id) {
          const url = `/proveedores/auditores/${asignacionMenu.auditor.id}`;
          if (e.originalEvent?.ctrlKey || e.originalEvent?.metaKey) {
            window.open(url, "_blank");
          } else {
            navigate(url);
          }
        }
      }
    },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (asignacionMenu) {
          confirmDialog({
            message: `¿Eliminar a ${asignacionMenu.auditor?.nombre} ${asignacionMenu.auditor?.apellido} del paso?`,
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí",
            rejectLabel: "No",
            accept: () => handleEliminarColaborador(asignacionMenu.id, pasoMenu)
          });
        }
      }
    }
  ];

  // Lista de colaboradores asignados a un paso, con su menú kebab
  function listaColaboradores(paso, refMenu) {
    const asignaciones = (paso.actividadpaso_x_auditor || []).filter(
      (a) => a.activo !== false
    );
    const administradores = paso.actividadpaso_x_administrador || [];

    if (asignaciones.length === 0 && administradores.length === 0) {
      return (
        <p css={{ color: "var(--grisTextos)" }}>
          No hay colaboradores asignados
        </p>
      );
    }

    return (
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem"
        }}
      >
        {asignaciones.map((asignacion) => (
          <div
            key={asignacion.id}
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.75rem",
              backgroundColor: "var(--surface-100)",
              borderRadius: "6px"
            }}
          >
            <div>
              <span css={{ fontWeight: "500" }}>
                {asignacion.auditor?.nombre} {asignacion.auditor?.apellido}
              </span>
              <Tag
                css={{
                  marginLeft: "0.5rem",
                  backgroundColor: asignacion.decisor
                    ? "var(--verde)"
                    : "var(--amarillo)",
                  color: "white"
                }}
                value={asignacion.decisor ? "Decisor" : "En formación"}
              />
            </div>
            <Boton
              texto
              negro
              callback={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  navigate(`/proveedores/auditores/${asignacion.auditor?.id}`);
                } else {
                  setAsignacionMenu(asignacion);
                  setPasoMenu(paso);
                  refMenu.current.toggle(e);
                }
              }}
            >
              <i className="pi pi-ellipsis-v" />
            </Boton>
          </div>
        ))}
        {administradores.map((asignacion) => {
          const rol = asignacion.usuario.Usuario_x_Acceso?.[0]?.acceso.nombre;
          const nombre = `${asignacion.usuario.nombre} ${asignacion.usuario.apellido}`;
          return (
            <div
              key={`admin-${asignacion.id}`}
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.75rem",
                backgroundColor: "var(--surface-100)",
                borderRadius: "6px"
              }}
            >
              <span css={{ fontWeight: "500" }}>
                {nombre}
                {rol ? ` (${rol})` : ""}
              </span>
              <Boton
                texto
                rojo
                confirmacion={`¿Eliminar a ${nombre} del paso?`}
                callback={() =>
                  handleEliminarAdministrador(asignacion.id, paso)
                }
              >
                <i className="pi pi-trash" />
              </Boton>
            </div>
          );
        })}
      </div>
    );
  }

  // Función para enviar decisión administrativa
  function handleDecisionAdmin(camposFormulario, aprobado) {
    if (!pasoSeleccionado) return;

    setLoaderDecisionAdmin(true);
    accessAPI(
      "PUT",
      `admin/actividad/paso/${pasoSeleccionado.id}/admin`,
      { ...camposFormulario, aprobado },
      (respuesta) => {
        // Recargar actividades para reflejar el cambio
        accessAPI(
          "GET",
          `admin/actividad/proceso/${procesoId}`,
          null,
          (response) => {
            setActividades(response);
            // Actualizar la actividad seleccionada
            const actividadActualizada = response.find(
              (a) => a.id === actividadSeleccionada.id
            );
            if (actividadActualizada) {
              setActividadSeleccionada(actividadActualizada);
              // Actualizar el paso seleccionado
              const pasoActualizado = actividadActualizada.actividadpaso?.find(
                (p) => p.id === pasoSeleccionado.id
              );
              if (pasoActualizado) {
                setPasoSeleccionado(pasoActualizado);
              }
            }
            setLoaderDecisionAdmin(false);
            setFormKeyDecisionAdmin((prev) => prev + 1);
            setSidebarPaso(false);
            toast.current?.show({
              severity: "success",
              summary: "Éxito",
              detail: aprobado ? "Paso aprobado" : "Paso rechazado"
            });
          },
          (error) => {
            setLoaderDecisionAdmin(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: error[0]?.msg || "Error al recargar actividades",
              sticky: true
            });
          }
        );
      },
      (error) => {
        setLoaderDecisionAdmin(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al enviar decisión",
          sticky: true
        });
      }
    );
  }

  // Configuración del formulario para decisión administrativa
  const formConfigDecisionAdmin = {
    secciones: [
      {
        nombre: "Decisión administrativa",
        campos: [
          {
            nombre: "comentarios",
            tipoitemformularioid: 1,
            etiqueta: "Comentarios"
          },
          {
            nombre: "fecha",
            tipoitemformularioid: 7,
            etiqueta: "Fecha (si está vacío, se toma hoy)"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Aprobar paso",
        clases: "verde",
        confirmacion: "¿Está seguro?",
        callback: (camposFormulario) => {
          handleDecisionAdmin(camposFormulario, true);
        }
      },
      {
        etiqueta: "Rechazar paso",
        clases: "rojo",
        confirmacion: "¿Está seguro?",
        callback: (camposFormulario) => {
          handleDecisionAdmin(camposFormulario, false);
        }
      }
    ]
  };

  // Función para calcular estado de planificación
  function calcularEstadoPlanificacion(fecha) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    let fechaFormat = new Date(fecha);
    const fechaPlanif = new Date(
      fechaFormat.getTime() + fechaFormat.getTimezoneOffset() * 60 * 1000
    );
    fechaPlanif.setHours(0, 0, 0, 0);

    const diff = Math.ceil((fechaPlanif - hoy) / (1000 * 60 * 60 * 24));

    if (diff < 0) {
      return { estado: "VENCIDO", color: "var(--rojo)", dias: diff };
    } else if (diff <= 7) {
      return { estado: "URGENTE", color: "var(--rojo)", dias: diff };
    } else if (diff <= 30) {
      return { estado: "PROXIMO", color: "var(--amarillo)", dias: diff };
    } else {
      return { estado: "PENDIENTE", color: "var(--verde)", dias: diff };
    }
  }

  // Función para crear planificación manual
  function handleCrearPlanificacion(camposFormulario) {
    setLoaderPlanificacion(true);
    accessAPI(
      "POST",
      `admin/planificacion/proceso/${proceso.id}/planificacion`,
      camposFormulario,
      (respuesta) => {
        setPlanificaciones((prev) => [...prev, respuesta.planificacion]);
        setSidebarPlanificacion(false);
        setFormKeyPlanificacion((prev) => prev + 1);
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Planificación creada correctamente"
        });
      },
      (error) => {
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al crear planificación",
          sticky: true
        });
      }
    );
  }

  // Función para aplicar plan de actividades
  function handleAplicarPlan(camposFormulario) {
    setLoaderPlanificacion(true);
    accessAPI(
      "POST",
      `admin/planificacion/proceso/${proceso.id}/aplicarplan`,
      camposFormulario,
      (respuesta) => {
        setPlanificaciones((prev) => [...prev, ...respuesta.planificaciones]);
        setSidebarPlanificacion(false);
        setFormKeyPlanificacion((prev) => prev + 1);
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg || "Plan aplicado correctamente"
        });
      },
      (error) => {
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al aplicar plan",
          sticky: true
        });
      }
    );
  }

  // Función para eliminar planificación
  function handleEliminarPlanificacion(planificacionId) {
    accessAPI(
      "DELETE",
      `admin/planificacion/planificacion/${planificacionId}`,
      null,
      () => {
        setPlanificaciones((prev) =>
          prev.filter((p) => p.id !== planificacionId)
        );
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Planificación eliminada"
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar planificación",
          sticky: true
        });
      }
    );
  }

  // Función para editar una planificación
  function handleEditarPlanificacion(camposFormulario) {
    if (!planificacionEditando) return;
    setLoaderPlanificacion(true);
    accessAPI(
      "PUT",
      `admin/planificacion/planificacion/${planificacionEditando.id}`,
      camposFormulario, // Payload: { actividadtipoid, fecha, comentario }
      (respuesta) => {
        setPlanificaciones((prev) =>
          prev.map((p) =>
            p.id === respuesta.planificacion.id ? respuesta.planificacion : p
          )
        );
        setSidebarPlanificacion(false);
        setPlanificacionEditando(null);
        setFormKeyPlanificacion((prev) => prev + 1);
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Planificación modificada correctamente"
        });
      },
      (error) => {
        setLoaderPlanificacion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al modificar planificación",
          sticky: true
        });
      }
    );
  }

  // Función para ejecutar planificación (crear actividad)
  function handleEjecutarPlanificacion(planificacion) {
    accessAPI(
      "POST",
      `admin/planificacion/planificacion/${planificacion.id}/ejecutar`,
      null,
      (respuesta) => {
        // Eliminar planificación de la lista
        setPlanificaciones((prev) =>
          prev.filter((p) => p.id !== planificacion.id)
        );
        // Recargar actividades
        accessAPI(
          "GET",
          `admin/actividad/proceso/${procesoId}`,
          null,
          (actividadesActualizadas) => {
            setActividades(actividadesActualizadas);
            const nuevaActividad = actividadesActualizadas.find(
              (a) => a.id === respuesta.actividad.id
            );
            if (nuevaActividad) {
              setActividadSeleccionada(nuevaActividad);
            }
          },
          () => {}
        );
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.msg || "Actividad generada correctamente"
        });
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al generar actividad",
          sticky: true
        });
      }
    );
  }

  // Configuración del formulario para planificación manual
  const formConfigPlanificacionManual = proceso
    ? {
        secciones: [
          {
            nombre: "Planificación manual",
            campos: [
              {
                nombre: "actividadtipoid",
                tipoitemformularioid: 5,
                etiqueta: "Tipo de actividad",
                requerido: true,
                opciones: `admin/actividad/actividadTipo?dropdown=true&certificadotipo=${proceso.certificadotipo.id}`,
                sinOpcionInicial: true
              },
              {
                nombre: "fecha",
                tipoitemformularioid: 7,
                etiqueta: "Fecha",
                requerido: true
              },
              {
                nombre: "comentario",
                tipoitemformularioid: 11,
                etiqueta: "Comentario"
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Crear planificación",
            clases: "verde",
            callback: handleCrearPlanificacion,
            validar: true
          }
        ]
      }
    : null;

  // Configuración del formulario para editar una planificación
  // Configuración del formulario para editar una actividad
  const formConfigEditarActividad = actividadMenu
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "fecha",
                tipoitemformularioid: 7,
                etiqueta: "Fecha",
                requerido: true,
                respuesta: actividadMenu.fechacreacion
              },
              {
                nombre: "comentarioplanificacion",
                tipoitemformularioid: 11,
                etiqueta: "Comentario",
                respuesta: actividadMenu.comentarioplanificacion ?? ""
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            clases: "verde",
            callback: handleEditarActividad,
            validar: true
          }
        ]
      }
    : null;

  const formConfigEditarPlanificacion =
    proceso && planificacionEditando
      ? {
          secciones: [
            {
              campos: [
                {
                  nombre: "actividadtipoid",
                  tipoitemformularioid: 5,
                  etiqueta: "Tipo de actividad",
                  requerido: true,
                  opciones: `admin/actividad/actividadTipo?dropdown=true&certificadotipo=${proceso.certificadotipo.id}`,
                  sinOpcionInicial: true,
                  respuesta: planificacionEditando.actividadtipo?.id
                },
                {
                  nombre: "fecha",
                  tipoitemformularioid: 7,
                  etiqueta: "Fecha",
                  requerido: true,
                  respuesta: planificacionEditando.fecha
                },
                {
                  nombre: "comentario",
                  tipoitemformularioid: 11,
                  etiqueta: "Comentario",
                  respuesta: planificacionEditando.comentario
                }
              ]
            }
          ],
          botones: [
            {
              etiqueta: "Guardar cambios",
              clases: "verde",
              callback: handleEditarPlanificacion,
              validar: true
            }
          ]
        }
      : null;

  // Configuración del formulario para aplicar plan
  const formConfigAplicarPlan = proceso
    ? {
        secciones: [
          {
            nombre: "Aplicar plan de actividades",
            campos: [
              {
                nombre: "plandeactividadesid",
                tipoitemformularioid: 5,
                etiqueta: "Plan de actividades",
                requerido: true,
                opciones: `admin/planificacion/planesactividades?dropdown=true&certificadotipoid=${proceso.certificadotipo.id}&activo=true`,
                sinOpcionInicial: true
              },
              {
                nombre: "fechainicio",
                tipoitemformularioid: 7,
                etiqueta:
                  "Fecha de inicio (opcional, si está vacío se toma hoy)"
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Aplicar plan",
            clases: "verde",
            callback: handleAplicarPlan,
            validar: true
          }
        ]
      }
    : null;

  // Sube el archivo del formulario con su categoría (subida directa a S3 vía
  // presigned URL) y confirma contra la API. La categoría custom, si se
  // escribió, pisa a la seleccionada en el dropdown.
  function subirArchivoActividad(contenidoCampos, archivo) {
    if (!archivo) {
      return;
    }
    setFormLoaderSubirArchivo(true);
    subirArchivosPesadosS3({
      presignEndpoint: "admin/archivo/presign",
      confirmarEndpoint: `admin/actividad/${actividadSeleccionada.id}/archivo/confirmar`,
      archivos: archivo,
      datosPresign: { ubicacion: "procesos" },
      datosConfirmar: {
        categoria:
          contenidoCampos.categoriaCustom?.trim() ||
          contenidoCampos.categoria ||
          null
      }
    })
      .then((creados) => {
        handleArchivosActividadSubidos(creados);
        setFormLoaderSubirArchivo(false);
        setSidebarSubirArchivo(false);
        toast.current?.show({
          severity: "success",
          summary: "Subido",
          detail: "Archivo subido exitosamente",
          life: 3000
        });
      })
      .catch((error) => {
        setFormLoaderSubirArchivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al subir archivo",
          sticky: true
        });
      });
  }

  // Guarda la nueva categoría del archivo seleccionado en el menú. La
  // categoría custom, si se escribió, pisa a la seleccionada en el dropdown.
  function editarCategoriaArchivo(contenidoCampos) {
    const categoria =
      contenidoCampos.categoriaCustom?.trim() ||
      contenidoCampos.categoria ||
      null;
    setFormLoaderCategoriaArchivo(true);
    accessAPI(
      "PUT",
      `admin/actividad/${actividadSeleccionada.id}/archivo/${archivoMenuSeleccionado.id}/categoria`,
      { categoria },
      (respuesta) => {
        const nuevosArchivos = (actividadSeleccionada.archivos || []).map(
          (a) =>
            a.id === archivoMenuSeleccionado.id
              ? { ...a, categoria: respuesta.categoria }
              : a
        );
        setActividadSeleccionada((prev) => ({
          ...prev,
          archivos: nuevosArchivos
        }));
        setActividades((prev) =>
          prev.map((a) =>
            a.id === actividadSeleccionada.id
              ? { ...a, archivos: nuevosArchivos }
              : a
          )
        );
        setFormLoaderCategoriaArchivo(false);
        setSidebarCategoriaArchivo(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Categoría actualizada correctamente",
          life: 3000
        });
      },
      (error) => {
        setFormLoaderCategoriaArchivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al actualizar categoría",
          sticky: true
        });
      }
    );
  }

  function handleArchivosActividadSubidos(respuesta) {
    const nuevosArchivos = [
      ...(actividadSeleccionada.archivos || []),
      ...respuesta
    ];
    setActividadSeleccionada((prev) => ({
      ...prev,
      archivos: nuevosArchivos
    }));
    setActividades((prev) =>
      prev.map((a) =>
        a.id === actividadSeleccionada.id
          ? { ...a, archivos: nuevosArchivos }
          : a
      )
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {/* Sidebar de subida de archivo de la actividad con categoría */}
      <Sidebar
        visible={sidebarSubirArchivo}
        onHide={() => setSidebarSubirArchivo(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Subir archivo</Titulo>
        {actividadSeleccionada && (
          <Formulario
            key={formKeySubirArchivo}
            config={{
              secciones: [
                {
                  campos: [
                    {
                      nombre: "categoria",
                      etiqueta: "Categoría",
                      tipoitemformularioid: 5, // dropdown
                      opciones: categoriasArchivos,
                      sinOpcionInicial: true
                    },
                    {
                      nombre: "categoriaCustom",
                      etiqueta: "Categoría custom",
                      tipoitemformularioid: 1,
                      notas: "Opcional, reemplaza a la seleccionada"
                    },
                    {
                      nombre: "archivo",
                      etiqueta: "Archivo",
                      tipoitemformularioid: 10, // file
                      requerido: true
                    }
                  ]
                }
              ],
              botones: [
                {
                  etiqueta: "Subir archivo",
                  callback: subirArchivoActividad,
                  validar: true
                }
              ]
            }}
            loader={formLoaderSubirArchivo}
          />
        )}
      </Sidebar>

      {/* Sidebar de edición de categoría de un archivo de la actividad */}
      <Sidebar
        visible={sidebarCategoriaArchivo}
        onHide={() => setSidebarCategoriaArchivo(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar categoría</Titulo>
        {archivoMenuSeleccionado && (
          <>
            <p css={{ marginTop: 0 }}>
              Categoría actual:{" "}
              <strong>
                {archivoMenuSeleccionado.categoria || "sin categoría"}
              </strong>
            </p>
            <Formulario
              key={formKeyCategoriaArchivo}
              config={{
                secciones: [
                  {
                    campos: [
                      {
                        nombre: "categoria",
                        etiqueta: "Categoría",
                        tipoitemformularioid: 5, // dropdown
                        opciones: categoriasArchivos,
                        sinOpcionInicial: true
                      },
                      {
                        nombre: "categoriaCustom",
                        etiqueta: "Categoría custom",
                        tipoitemformularioid: 1,
                        notas: "Opcional, reemplaza a la seleccionada"
                      }
                    ]
                  }
                ],
                botones: [
                  {
                    etiqueta: "Guardar categoría",
                    callback: editarCategoriaArchivo
                  }
                ]
              }}
              loader={formLoaderCategoriaArchivo}
            />
          </>
        )}
      </Sidebar>
      <Menu
        ref={menuPlanificacion}
        popup
        model={[
          {
            label: "Crear actividad",
            icon: "pi pi-plus",
            command: () => {
              if (planificacionMenu) {
                confirmDialog({
                  message: "¿Crear actividad a partir de esta planificación?",
                  header: "Confirmar",
                  icon: "pi pi-question-circle",
                  acceptLabel: "Sí",
                  rejectLabel: "No",
                  accept: () => handleEjecutarPlanificacion(planificacionMenu)
                });
              }
            }
          },
          {
            label: "Editar",
            icon: "pi pi-pencil",
            command: () => {
              if (planificacionMenu) {
                setPlanificacionEditando(planificacionMenu);
                setSidebarPlanificacion(true);
              }
            }
          },
          {
            label: "Eliminar",
            icon: "pi pi-trash",
            className: "p-menuitem-danger",
            command: () => {
              if (planificacionMenu) {
                confirmDialog({
                  message: "¿Eliminar esta planificación?",
                  header: "Confirmar eliminación",
                  icon: "pi pi-exclamation-triangle",
                  acceptLabel: "Sí",
                  rejectLabel: "No",
                  accept: () =>
                    handleEliminarPlanificacion(planificacionMenu.id)
                });
              }
            }
          }
        ]}
      />
      <Menu
        ref={menuActividad}
        popup
        model={[
          {
            label: "Editar",
            icon: "pi pi-pencil",
            command: () => {
              if (actividadMenu) {
                setFormKeyEditarActividad((k) => k + 1);
                setSidebarEditarActividad(true);
              }
            }
          },
          // Mismas condiciones que tenía el botón del título: sólo se puede
          // eliminar una actividad que nunca se trabajó
          ...(actividadEsEliminable(actividadMenu)
            ? [
                {
                  label: "Eliminar",
                  icon: "pi pi-trash",
                  className: "p-menuitem-danger",
                  command: () => {
                    if (actividadMenu) {
                      confirmDialog({
                        message:
                          "¿Eliminar esta actividad? Esta acción no se puede deshacer.",
                        header: "Confirmar eliminación",
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Eliminar",
                        rejectLabel: "Cancelar",
                        acceptClassName: "p-button-danger",
                        accept: () => handleEliminarActividad(actividadMenu)
                      });
                    }
                  }
                }
              ]
            : [])
        ]}
      />
      <Menu
        ref={menuArchivo}
        popup
        model={[
          {
            label: "Descargar",
            icon: "pi pi-download",
            command: () => {
              if (archivoMenuSeleccionado) {
                setDescargandoArchivoId(archivoMenuSeleccionado.id);
                downloadFile(
                  `admin/archivo/${archivoMenuSeleccionado.id}`,
                  archivoMenuSeleccionado.nombredescarga,
                  () => setDescargandoArchivoId(null),
                  (error) => {
                    setDescargandoArchivoId(null);
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: error[0]?.msg || "Error al descargar",
                      sticky: true
                    });
                  }
                );
              }
            }
          },
          {
            label: "Editar categoría",
            icon: "pi pi-bookmark",
            command: () => {
              if (archivoMenuSeleccionado) {
                setFormKeyCategoriaArchivo((k) => k + 1);
                setSidebarCategoriaArchivo(true);
              }
            }
          },
          {
            label: "Mover a proceso",
            icon: "pi pi-arrow-right",
            command: () => {
              if (archivoMenuSeleccionado && actividadSeleccionada) {
                accessAPI(
                  "POST",
                  `admin/actividad/${actividadSeleccionada.id}/archivo/${archivoMenuSeleccionado.id}/mover-a-proceso`,
                  null,
                  () => {
                    toast.current?.show({
                      severity: "success",
                      summary: "Movido",
                      detail: "Archivo movido al proceso"
                    });
                    const nuevosArchivos = (actividadSeleccionada.archivos || []).filter(
                      (a) => a.id !== archivoMenuSeleccionado.id
                    );
                    setActividadSeleccionada((prev) => ({
                      ...prev,
                      archivos: nuevosArchivos
                    }));
                    setActividades((prev) =>
                      prev.map((a) =>
                        a.id === actividadSeleccionada.id
                          ? { ...a, archivos: nuevosArchivos }
                          : a
                      )
                    );
                  },
                  (error) => {
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: error[0]?.msg || "Error al mover archivo",
                      sticky: true
                    });
                  }
                );
              }
            }
          },
          {
            label: archivoMenuSeleccionado?.disponibleParaClientes
              ? "Quitar de clientes"
              : "Disponible para clientes",
            icon: archivoMenuSeleccionado?.disponibleParaClientes
              ? "pi pi-eye-slash"
              : "pi pi-eye",
            command: () => {
              if (archivoMenuSeleccionado && actividadSeleccionada) {
                accessAPI(
                  "PUT",
                  `admin/actividad/${actividadSeleccionada.id}/archivo/${archivoMenuSeleccionado.id}/disponibilidadclientes`,
                  null,
                  (respuesta) => {
                    toast.current?.show({
                      severity: "success",
                      summary: "Actualizado",
                      detail: respuesta.disponibleParaClientes
                        ? "Archivo disponible para clientes"
                        : "Archivo no disponible para clientes"
                    });
                    const nuevosArchivos = (actividadSeleccionada.archivos || []).map(
                      (a) => a.id === archivoMenuSeleccionado.id
                        ? { ...a, disponibleParaClientes: respuesta.disponibleParaClientes }
                        : a
                    );
                    setActividadSeleccionada((prev) => ({
                      ...prev,
                      archivos: nuevosArchivos
                    }));
                    setActividades((prev) =>
                      prev.map((a) =>
                        a.id === actividadSeleccionada.id
                          ? { ...a, archivos: nuevosArchivos }
                          : a
                      )
                    );
                  },
                  (error) => {
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: error[0]?.msg || "Error al actualizar disponibilidad",
                      sticky: true
                    });
                  }
                );
              }
            }
          },
          {
            separator: true
          },
          {
            label: "Eliminar",
            icon: "pi pi-trash",
            className: "p-menuitem-danger",
            command: () => {
              if (archivoMenuSeleccionado && actividadSeleccionada) {
                confirmDialog({
                  message: `¿Eliminar "${archivoMenuSeleccionado.nombredescarga}"?`,
                  header: "Confirmar eliminación",
                  icon: "pi pi-exclamation-triangle",
                  acceptLabel: "Eliminar",
                  rejectLabel: "Cancelar",
                  acceptClassName: "p-button-danger",
                  accept: () => {
                    accessAPI(
                      "DELETE",
                      `admin/actividad/${actividadSeleccionada.id}/archivo/${archivoMenuSeleccionado.id}`,
                      null,
                      () => {
                        toast.current?.show({
                          severity: "success",
                          summary: "Eliminado",
                          detail: "Archivo eliminado"
                        });
                        const nuevosArchivos = (actividadSeleccionada.archivos || []).filter(
                          (a) => a.id !== archivoMenuSeleccionado.id
                        );
                        setActividadSeleccionada((prev) => ({
                          ...prev,
                          archivos: nuevosArchivos
                        }));
                        setActividades((prev) =>
                          prev.map((a) =>
                            a.id === actividadSeleccionada.id
                              ? { ...a, archivos: nuevosArchivos }
                              : a
                          )
                        );
                      },
                      (error) => {
                        toast.current?.show({
                          severity: "error",
                          summary: "Error",
                          detail: error[0]?.msg || "Error al eliminar",
                          sticky: true
                        });
                      }
                    );
                  }
                });
              }
            }
          }
        ]}
      />
      {loader && (
        <div>
          <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "2em", marginBottom: "1em" }}>
            <Skeleton width="16rem" height="2rem" />
          </div>
          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton width="12rem" height="1.5rem" css={{ marginBottom: "1em" }} />
                <Skeleton width="100%" height="2.5rem" css={{ marginBottom: "0.5em" }} />
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} width="100%" height="2.5rem" css={{ marginBottom: "0.3em" }} />
                ))}
              </Card>
              <Card css={{ marginTop: "1rem" }}>
                <Skeleton width="10rem" height="1.5rem" css={{ marginBottom: "1em" }} />
                <Skeleton width="100%" height="2.5rem" css={{ marginBottom: "0.5em" }} />
                {[1, 2].map((i) => (
                  <Skeleton key={i} width="100%" height="2.5rem" css={{ marginBottom: "0.3em" }} />
                ))}
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton width="10rem" height="1.5rem" css={{ marginBottom: "1.5em" }} />
                <Skeleton width="100%" height="3.5rem" css={{ marginBottom: "0.5em" }} />
                <Skeleton width="100%" height="3.5rem" css={{ marginBottom: "0.5em" }} />
                <Skeleton width="100%" height="3.5rem" />
              </Card>
            </div>
          </div>
        </div>
      )}
      {!loader && proceso && (
        <>
          <Titulo
            urlVolver={urlVolver}
            subtitulo={`${proceso.cliente.razonsocial} - ${proceso.nombrefantasia}`}
          >
            Proceso {proceso.nombre} - Actividades
          </Titulo>

          <div className="grid">
            {/* Panel izquierdo - Lista de actividades y planificaciones */}
            {/* Sin margen propio: la separación entre cards la da el grid (1em) */}
            <div className="col-12 lg:col-6" css={{ overflow: "auto" }}>
              <Card>
                <Titulo
                  botones={
                    <Boton texto negro callback={() => setSidebarAgregar(true)}>
                      <i className="pi pi-plus"></i>
                    </Boton>
                  }
                >
                  Actividades del proceso
                </Titulo>

                <TablaFiltrable conFiltros={false}
                  value={actividades}
                  size="small"
                  selectionMode="single"
                  selection={actividadSeleccionada}
                  onSelectionChange={(e) => setActividadSeleccionada(e.value)}
                  onRowSelect={onRowSelect}
                  dataKey="id"
                  emptyMessage="No hay actividades en este proceso"
                  scrollable
                  scrollHeight="flex"
                  nombreArchivo="actividades_proceso"
                >
                  <Column
                    body={estadoTemplate}
                    style={{ width: "40px", textAlign: "center" }}
                  />
                  <Column
                    header="Tipo"
                    body={tipoTemplate}
                    style={{ minWidth: "150px" }}
                  />
                  <Column
                    header="Creado"
                    body={fechaTemplate}
                    style={{ width: "100px" }}
                  />
                  <Column
                    body={comentarioTemplate}
                    style={{ width: "50px", textAlign: "center" }}
                  />
                  <Column
                    body={(rowData) => (
                      <Boton
                        texto
                        negro
                        callback={(e) => {
                          // Evita que el click también seleccione la fila
                          e.stopPropagation?.();
                          setActividadMenu(rowData);
                          menuActividad.current.toggle(e);
                        }}
                      >
                        <i className="pi pi-ellipsis-v" />
                      </Boton>
                    )}
                    style={{ width: "60px", textAlign: "center" }}
                  />
                </TablaFiltrable>
              </Card>

              {/* Planificaciones */}
              <Card css={{ marginTop: "1em" }}>
                <Titulo
                  botones={
                    <Boton
                      texto
                      negro
                      callback={() => {
                        setPlanificacionEditando(null);
                        setSidebarPlanificacion(true);
                      }}
                    >
                      <i className="pi pi-plus"></i>
                    </Boton>
                  }
                >
                  Planificaciones
                </Titulo>

                <TablaFiltrable conFiltros={false}
                  value={planificaciones}
                  size="small"
                  dataKey="id"
                  emptyMessage="No hay planificaciones activas"
                  nombreArchivo="planificaciones_proceso"
                >
                  <Column
                    header="Actividad"
                    field="actividadtipo.nombre"
                    style={{ minWidth: "120px" }}
                  />
                  {!isMobile && (
                    <Column
                      header="Fecha"
                      body={(rowData) => formatFecha(rowData.fecha)}
                      style={{ width: "100px" }}
                    />
                  )}
                  <Column
                    header="Días"
                    alignHeader="center"
                    body={(rowData) => {
                      const { dias } = calcularEstadoPlanificacion(
                        rowData.fecha
                      );
                      if (dias < 0) {
                        return <Tag value={`${Math.abs(dias)} atrás`} severity="danger" />;
                      }
                      if (dias === 0) {
                        return <Tag value="Hoy" severity="danger" />;
                      }
                      if (dias <= 7) {
                        return <Tag value={`${dias}`} severity="warning" />;
                      }
                      return <Tag value={`${dias}`} severity="info" />;
                    }}
                    style={{ width: "100px" }}
                    bodyStyle={{ textAlign: "center" }}
                  />
                  <Column
                    body={(rowData) => {
                      const tooltipId = `planificacion-comentario-${rowData.id}`;
                      return (
                        <div css={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.25rem" }}>
                          {rowData.comentario && (
                            <>
                              <Tooltip
                                target={`#${tooltipId}`}
                                content={rowData.comentario}
                                position="top"
                              />
                              <i
                                id={tooltipId}
                                className="pi pi-info-circle"
                                style={{
                                  color: "var(--amarillo)",
                                  fontSize: "1.1rem",
                                  cursor: "pointer"
                                }}
                              />
                            </>
                          )}
                          <Boton
                            texto
                            negro
                            callback={(e) => {
                              setPlanificacionMenu(rowData);
                              menuPlanificacion.current.toggle(e);
                            }}
                          >
                            <i className="pi pi-ellipsis-v" />
                          </Boton>
                        </div>
                      );
                    }}
                    style={{ width: "60px" }}
                  />
                </TablaFiltrable>
              </Card>
            </div>

            {/* Panel derecho - Detalles de actividad */}
            <div className="col-12 lg:col-6" css={{ marginBottom: "50px" }}>
              {actividadSeleccionada ? (
                <Card>
                  <div>
                    <Titulo
                      botones={
                        <>
                          {actividadSeleccionada?.actividadtipo
                            ?.instruccionesAlAsistente &&
                            (analizandoIA ||
                            analisisIA?.estado === "procesando" ? (
                            // Analizando: un solo botón "Cancelar" con el loader.
                            <Boton
                              rojo
                              transparente
                              confirmacion="¿Cancelar el análisis en curso?"
                              callback={() => cancelarAnalisisIA("usuario")}
                            >
                              <i
                                className="pi pi-spin pi-spinner"
                                css={{ marginRight: "0.4em" }}
                              />
                              Cancelar
                            </Boton>
                          ) : analisisIA &&
                            ["recibido", "revisado", "volcado"].includes(
                              analisisIA.estado
                            ) ? (
                            <Boton
                              verde
                              transparente
                              callback={() => setSidebarAnalisisIA(true)}
                              menu={[
                                {
                                  etiqueta: "Repetir análisis",
                                  icono: "pi pi-refresh",
                                  callback: () => analizarConIA()
                                },
                                {
                                  etiqueta: "Repetir con prompt",
                                  icono: "pi pi-pencil",
                                  callback: abrirPromptSidebar
                                }
                              ]}
                            >
                              Ver análisis
                            </Boton>
                          ) : (
                            <Boton
                              verde
                              transparente
                              callback={() => analizarConIA()}
                              menu={[
                                {
                                  etiqueta: "Analizar con prompt",
                                  icono: "pi pi-pencil",
                                  callback: abrirPromptSidebar
                                }
                              ]}
                            >
                              Analizar con IA
                            </Boton>
                          ))}
                        </>
                      }
                    >
                      {actividadSeleccionada.actividadtipo?.nombre}
                    </Titulo>
                    {actividadSeleccionada.comentarioplanificacion && (
                      <p
                        css={{
                          color: "var(--grisTextos)",
                          marginTop: "0.5rem"
                        }}
                      >
                        {actividadSeleccionada.comentarioplanificacion}
                      </p>
                    )}

                    <Accordion
                      multiple
                      activeIndex={[0]}
                      transitionOptions={{ timeout: 150 }}
                      css={{
                        marginTop: "1rem",
                        ".p-accordion-header-link": {
                          backgroundColor: "white !important",
                          color: "var(--negro) !important"
                        }
                      }}
                    >
                      {/* Timeline de pasos */}
                      <AccordionTab header="Pasos">
                        <Titulo
                          botones={
                            <Boton
                              transparente
                              callback={() => {
                                setFormKeyColaborador((k) => k + 1);
                                setSidebarColaboradores(true);
                              }}
                            >
                              Colaboradores
                            </Boton>
                          }
                        />
                        {/* Flujograma real de la actividad, con los pasos
                            abiertos marcados y las decisiones de cada paso
                            dentro de su caja */}
                        <FlujoActividad
                          actividad={actividadSeleccionada}
                          extraDePaso={bloqueEnsayo}
                          onVerDetalles={(paso, decisiones) =>
                            // El detalle de revisión (productos) se carga si
                            // el paso tuvo alguna decisión
                            handleClickPaso(
                              paso,
                              decisiones[decisiones.length - 1]
                            )
                          }
                        />
                      </AccordionTab>

                      {/* Archivos de la actividad */}
                      <AccordionTab header="Archivos">
                        <Titulo
                          botones={
                            <>
                              {/* Subida múltiple sin categoría: filepicker directo */}
                              <SubidorArchivos
                                presignEndpoint="admin/archivo/presign"
                                confirmarEndpoint={`admin/actividad/${actividadSeleccionada.id}/archivo/confirmar`}
                                ubicacion="procesos"
                                multiple
                                chooseLabel="Multi"
                                texto
                                onSubido={handleArchivosActividadSubidos}
                              />
                              <Boton
                                icono
                                negro
                                callback={() => {
                                  setFormKeySubirArchivo((k) => k + 1);
                                  setSidebarSubirArchivo(true);
                                }}
                              >
                                <i className="pi pi-plus"></i>
                              </Boton>
                            </>
                          }
                        />
                        {(() => {
                          const archivosActividad = actividadSeleccionada.archivos || [];
                          if (archivosActividad.length === 0) {
                            return (
                              <p css={{ color: "var(--grisTextos)", margin: 0 }}>
                                No hay archivos en esta actividad
                              </p>
                            );
                          }

                          return (
                            <TablaFiltrable conFiltros={false} key={`archivos-${descargandoArchivoId}`} value={archivosActividad} size="small" nombreArchivo="archivos_actividad">
                              <Column
                                field="nombredescarga"
                                header="Archivo"
                                body={(rowData) => (
                                  <ArchivoPreview
                                    nombre={rowData.nombredescarga}
                                    hash={rowData.hash}
                                    etiqueta={rowData.categoria || undefined}
                                    subtitulo={rowData.categoria ? rowData.nombredescarga : undefined}
                                  />
                                )}
                              />
                              <Column
                                header="Cli."
                                body={(rowData) =>
                                  rowData.disponibleParaClientes
                                    ? <i className="pi pi-check" css={{ color: "var(--verde)" }} />
                                    : null
                                }
                                style={{ width: "4rem", textAlign: "center" }}
                              />
                              <Column
                                body={(rowData) => {
                                  const descargando = descargandoArchivoId === rowData.id;
                                  return (
                                    <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
                                      {descargando && <i className="pi pi-spin pi-spinner" />}
                                      <Boton
                                        icono
                                        negro
                                        callback={(e) => {
                                          setArchivoMenuSeleccionado(rowData);
                                          menuArchivo.current.toggle(e);
                                        }}
                                      >
                                        <i className="pi pi-ellipsis-v" />
                                      </Boton>
                                    </div>
                                  );
                                }}
                                style={{ width: "50px", textAlign: "center" }}
                              />
                            </TablaFiltrable>
                          );
                        })()}
                      </AccordionTab>

                      {/* Facturas emitidas del proceso. Solo para quien puede ver
                          facturación */}
                      {puedeVerFacturacion && (
                      <AccordionTab header="Facturación">
                        <div className="grid">
                          <CampoInformacion nombreCampo="Última facturación">
                            {actividadSeleccionada.fechaUltimaFacturacion
                              ? formatFecha(
                                  actividadSeleccionada.fechaUltimaFacturacion
                                )
                              : "Sin facturar"}
                          </CampoInformacion>
                          <CampoInformacion nombreCampo="Frecuencia de facturación">
                            {actividadSeleccionada.frecuenciaRefacturacion
                              ? FRECUENCIA_LABEL[
                                  actividadSeleccionada.frecuenciaRefacturacion
                                ] || actividadSeleccionada.frecuenciaRefacturacion
                              : "Sin recurrencia"}
                          </CampoInformacion>
                        </div>
                        {facturasProceso === null ? (
                          <Skeleton height="4rem" />
                        ) : facturasProceso.length === 0 ? (
                          <p css={{ color: "var(--grisTextos)", margin: 0 }}>
                            No hay facturas emitidas para este proceso
                          </p>
                        ) : (
                          <TablaFiltrable conFiltros={false} value={facturasProceso} size="small" nombreArchivo="facturas_proceso">
                            <Column
                              header="Fecha"
                              body={(rowData) => formatFecha(rowData.fechaCreacion)}
                            />
                            <Column
                              header="Número de factura"
                              body={(rowData) => (
                                <span css={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                                  {rowData.numeroFactura || "—"}
                                  {rowData.anulada && (
                                    <Tag value="Anulada" severity="danger" css={{ fontSize: "0.7em" }} />
                                  )}
                                </span>
                              )}
                            />
                          </TablaFiltrable>
                        )}
                      </AccordionTab>
                      )}

                      {/* Hallazgos */}
                      {actividadSeleccionada.hallazgos?.length > 0 && (
                        <AccordionTab
                          header={
                            <span css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              Hallazgos
                              {actividadSeleccionada.hallazgos.filter((h) => !h.fechaCierre).length > 0 && (
                                <Tag
                                  value={`${actividadSeleccionada.hallazgos.filter((h) => !h.fechaCierre).length} abierto${actividadSeleccionada.hallazgos.filter((h) => !h.fechaCierre).length > 1 ? "s" : ""}`}
                                  severity="danger"
                                  css={{ fontSize: "0.7em" }}
                                />
                              )}
                            </span>
                          }
                        >
                          <TablaFiltrable conFiltros={false}
                            value={actividadSeleccionada.hallazgos}
                            emptyMessage="No hay hallazgos"
                            size="small"
                            nombreArchivo="hallazgos_actividad"
                          >
                            <Column field="resumen" header="Resumen" />
                            <Column
                              header="Tipo"
                              body={(rowData) => (
                                <span css={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                                  {rowData.hallazgoTipo?.nombre}
                                  {!rowData.fechaCierre && (
                                    <Tag value="Abierto" severity="danger" css={{ fontSize: "0.7em" }} />
                                  )}
                                </span>
                              )}
                            />
                            <Column
                              body={accionesHallazgo}
                              style={{ width: "3em" }}
                            />
                          </TablaFiltrable>
                        </AccordionTab>
                      )}

                      {/* Análisis de riesgo - solo para actividades de emisión */}
                      {actividadSeleccionada.actividadtipo?.correspondeemision && (
                        <AccordionTab
                          header={
                            <span css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              Análisis de Riesgo
                              {(actividadSeleccionada.analisisriesgo?.riesgoTotal === undefined ||
                                actividadSeleccionada.analisisriesgo?.riesgoTotal >= 100) && (
                                <i
                                  className="pi pi-exclamation-triangle"
                                  css={{
                                    color: actividadSeleccionada.analisisriesgo?.riesgoTotal >= 100
                                      ? "var(--rojo)"
                                      : "var(--amarillo)"
                                  }}
                                />
                              )}
                            </span>
                          }
                        >
                          <AnalisisRiesgo
                            actividad={actividadSeleccionada}
                            toast={toast}
                            onActualizado={(nuevoAnalisis) => {
                              // Actualizar la actividad en la lista
                              setActividades((prev) =>
                                prev.map((a) =>
                                  a.id === actividadSeleccionada.id
                                    ? { ...a, analisisriesgo: nuevoAnalisis }
                                    : a
                                )
                              );
                              // Actualizar la actividad seleccionada
                              setActividadSeleccionada((prev) => ({
                                ...prev,
                                analisisriesgo: nuevoAnalisis
                              }));
                            }}
                          />
                        </AccordionTab>
                      )}
                    </Accordion>
                  </div>
                </Card>
              ) : (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--grisTextos)"
                  }}
                >
                  <p>
                    <i>Seleccione una actividad para ver sus detalles</i>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Análisis de IA (solo lectura) */}
          <Sidebar
            visible={sidebarAnalisisIA}
            onHide={() => setSidebarAnalisisIA(false)}
            position="right"
            className="w-full lg:w-8"
          >
            <AnalisisIA analisis={analisisIA} proceso={proceso} />
          </Sidebar>

          {/* Sidebar Analizar con prompt destacado */}
          <Sidebar
            visible={sidebarPromptIA}
            onHide={() => setSidebarPromptIA(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <div css={{ marginBottom: "50px" }}>
              <Titulo>Analizar con prompt</Titulo>
              <p css={{ color: "var(--grisTextos)" }}>
                Escribí una instrucción destacada para este análisis particular.
                Se envía al asistente además de la configuración habitual.
              </p>
              <Formulario key={formPromptKey} config={formConfigPromptIA} />
            </div>
          </Sidebar>

          {/* Sidebar Agregar Actividad */}
          <Sidebar
            visible={sidebarAgregar}
            onHide={() => setSidebarAgregar(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Nueva actividad</Titulo>
            {formConfigAgregar && (
              <Formulario
                key={formKey}
                config={formConfigAgregar}
                loader={loaderAgregar}
              />
            )}
          </Sidebar>

          {/* Sidebar Editar Actividad */}
          <Sidebar
            visible={sidebarEditarActividad}
            onHide={() => setSidebarEditarActividad(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar actividad</Titulo>
            {actividadMenu && formConfigEditarActividad && (
              <Formulario
                key={`editar-actividad-${actividadMenu.id}-${formKeyEditarActividad}`}
                config={formConfigEditarActividad}
                loader={loaderEditarActividad}
              />
            )}
          </Sidebar>

          {/* Sidebar Agregar/Editar Planificación */}
          <Sidebar
            visible={sidebarPlanificacion}
            onHide={() => {
              setSidebarPlanificacion(false);
              setPlanificacionEditando(null);
            }}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>
              {planificacionEditando
                ? "Editar planificación"
                : "Nueva planificación"}
            </Titulo>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
            >
              {planificacionEditando ? (
                formConfigEditarPlanificacion && (
                  <Formulario
                    key={`editar-${planificacionEditando.id}-${formKeyPlanificacion}`}
                    config={formConfigEditarPlanificacion}
                    loader={loaderPlanificacion}
                    formularioEnCuerpo
                  />
                )
              ) : (
                <>
                  {formConfigPlanificacionManual && (
                    <Formulario
                      key={`manual-${formKeyPlanificacion}`}
                      config={formConfigPlanificacionManual}
                      loader={loaderPlanificacion}
                      formularioEnCuerpo
                    />
                  )}
                  <Divider />
                  {formConfigAplicarPlan && (
                    <Formulario
                      key={`plan-${formKeyPlanificacion}`}
                      config={formConfigAplicarPlan}
                      loader={loaderPlanificacion}
                      formularioEnCuerpo
                    />
                  )}
                </>
              )}
            </div>
          </Sidebar>

          {/* Sidebar del ensayo de un paso de tipo Ensayo */}
          <Sidebar
            visible={!!pasoEnsayo}
            onHide={() => setPasoEnsayo(null)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            {pasoEnsayo && (
              <>
                <Titulo>
                  {pasoEnsayo.ensayo ? "Editar ensayo" : "Asignar ensayo"}
                </Titulo>
                <Formulario
                  key={`ensayo-${pasoEnsayo.id}`}
                  config={{
                    secciones: [
                      {
                        nombre: pasoEnsayo.nombre,
                        campos: [
                          {
                            nombre: "ensayoId",
                            tipoitemformularioid: 5,
                            etiqueta: "Número de ensayo",
                            opciones: `admin/actividad/paso/${pasoEnsayo.id}/ensayos`,
                            // Arranca en el valor actual del paso y se puede
                            // vaciar para dejarlo sin asignar
                            sinOpcionInicial: true,
                            opcionVacia: true,
                            respuesta: pasoEnsayo.ensayoExterno
                              ? "externo"
                              : pasoEnsayo.ensayo?.id || null
                          }
                        ]
                      }
                    ],
                    botones: [
                      { etiqueta: "Guardar", callback: handleGuardarEnsayo }
                    ]
                  }}
                  loader={loaderEnsayo}
                />
              </>
            )}
          </Sidebar>

          {/* Sidebar Paso */}
          <Sidebar
            visible={sidebarPaso}
            onHide={() => {
              setSidebarPaso(false);
              setProductosRevision(null);
              setImagenesVerificaciones({});
            }}
            position="right"
            className="w-full lg:w-8"
          >
            {pasoSeleccionado && (
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem"
                }}
              >
                <Titulo>{pasoSeleccionado.nombre}</Titulo>

                {/* Todas las decisiones del paso, con su comentario completo */}
                {decisionesDePaso(pasoSeleccionado).length > 0 && (
                  <div>
                    <h4 css={{ marginBottom: "0.75rem" }}>
                      <i
                        className="pi pi-check-square"
                        css={{ marginRight: "0.5em" }}
                      ></i>
                      Decisiones
                    </h4>
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75em"
                      }}
                    >
                      {decisionesDePaso(pasoSeleccionado).map((item) => (
                        <div
                          key={`${item.esAdmin ? "admin" : "colab"}-${
                            item.decision.id
                          }`}
                          css={{
                            padding: "0.75em",
                            backgroundColor: item.decision.aprobado
                              ? "rgba(76, 175, 80, 0.08)"
                              : "rgba(244, 67, 54, 0.08)",
                            borderRadius: "6px",
                            borderLeft: `3px solid ${
                              item.decision.aprobado
                                ? "var(--verde)"
                                : "var(--rojo)"
                            }`
                          }}
                        >
                          <div
                            css={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "1em"
                            }}
                          >
                            <small css={{ fontWeight: "600" }}>
                              {formatFecha(item.decision.fechadecision)} ·{" "}
                              {item.nombre}
                              {item.esAdmin ? " (decisión administrativa)" : ""}
                            </small>
                            <Tag
                              value={
                                item.decision.aprobado ? "Aprobado" : "Rechazado"
                              }
                              severity={
                                item.decision.aprobado ? "success" : "danger"
                              }
                            />
                          </div>
                          {item.decision.comentarios && (
                            <p
                              css={{
                                margin: "0.5em 0 0",
                                whiteSpace: "pre-wrap",
                                fontSize: "0.9rem"
                              }}
                            >
                              {item.decision.comentarios}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detalle de revisión */}
                {loaderRevision && (
                  <div>
                    <h4 css={{ marginBottom: "0.75rem" }}>
                      <i className="pi pi-search" css={{ marginRight: "0.5em" }}></i>
                      Detalle de revisión
                    </h4>
                    <Skeleton width="100%" height="3rem" css={{ marginBottom: "0.5rem" }} />
                    <Skeleton width="100%" height="5rem" css={{ marginBottom: "0.5rem" }} />
                    <Skeleton width="100%" height="5rem" />
                  </div>
                )}
                {!loaderRevision && productosRevision && (() => {
                  // Solo lo que se hizo en este paso: de cada producto, los
                  // campos que tienen alguna verificación (la API ya devuelve
                  // únicamente las de este paso) o algún cambio de valor
                  // hecho acá, y solo los productos con algún campo así.
                  const productos = (
                    productosRevision.actividad?.proceso?.producto || []
                  )
                    .map((producto) => ({
                      ...producto,
                      producto_x_productocampospropios: (
                        producto.producto_x_productocampospropios || []
                      )
                        .map((campo) => ({
                          ...campo,
                          historico: (campo.historico || []).filter(
                            (cambio) => cambio.pasoId === pasoSeleccionado.id
                          )
                        }))
                        .filter(
                          (campo) =>
                            campo.ComentarioCampo?.length > 0 ||
                            campo.historico.length > 0
                        )
                    }))
                    .filter(
                      (producto) =>
                        producto.producto_x_productocampospropios.length > 0
                    );

                  return (
                    <div>
                      <h4 css={{ marginBottom: "0.75rem" }}>
                        <i className="pi pi-search" css={{ marginRight: "0.5em" }}></i>
                        Detalle de revisión
                      </h4>

                      {/* Productos con campos */}
                      {productos.map((producto, idx) => (
                        <div key={producto.id}>
                          {idx > 0 && (
                            <div css={{
                              borderTop: "3px solid var(--surface-300)",
                              margin: "1.5em 0"
                            }} />
                          )}
                          {producto.vigente === 0 && (
                            <Tag value="No vigente" severity="secondary" css={{ marginBottom: "0.5em" }} />
                          )}

                          {producto.producto_x_productocampospropios?.map((campo) => (
                            <div
                              key={campo.id}
                              css={{
                                marginBottom: "1em",
                                padding: "0.75em",
                                backgroundColor: "white",
                                borderRadius: "6px",
                                border: "1px solid #e0e0e0"
                              }}
                            >
                              <div css={{ marginBottom: "0.5em" }}>
                                <strong css={{ color: "var(--grisTextos)", fontSize: "0.85em" }}>
                                  {campo.productocampospropios.etiqueta}
                                </strong>
                              </div>
                              <div css={{ fontSize: "1em", marginBottom: "0.5em" }}>
                                {campo.valor
                                  ? `${campo.valor}${campo.productocampospropios?.unidades ? ` ${campo.productocampospropios.unidades}` : ""}`
                                  : <span css={{ color: "#999" }}>N/A</span>}
                              </div>

                              {/* Histórico de valores */}
                              {campo.historico && campo.historico.length > 0 && (
                                <div css={{ marginTop: "0.5em" }}>
                                  <small css={{ color: "var(--grisTextos)", fontWeight: "600", display: "block", marginBottom: "0.5em" }}>
                                    <i className="pi pi-history" css={{ marginRight: "0.5em" }}></i>
                                    Histórico
                                  </small>
                                  <div css={{ display: "flex", flexDirection: "column", gap: "0.4em" }}>
                                    {campo.historico.map((hist, idx) => {
                                      const esDeEstePaso = hist.pasoId === pasoSeleccionado.id;
                                      return (
                                        <div
                                          key={idx}
                                          css={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "0.5em",
                                            padding: "0.4em 0.5em",
                                            backgroundColor: esDeEstePaso
                                              ? hist.aprobacion === true
                                                ? "rgba(76, 175, 80, 0.15)"
                                                : hist.aprobacion === false
                                                  ? "rgba(244, 67, 54, 0.15)"
                                                  : "rgba(158, 158, 158, 0.15)"
                                              : "var(--surface-100)",
                                            borderRadius: "4px",
                                            borderLeft: `3px solid ${
                                              hist.aprobacion === true
                                                ? "var(--verde)"
                                                : hist.aprobacion === false
                                                  ? "var(--rojo)"
                                                  : "#9e9e9e"
                                            }`,
                                            opacity: esDeEstePaso ? 1 : 0.7
                                          }}
                                        >
                                          <i
                                            className={
                                              hist.aprobacion === true
                                                ? "pi pi-check"
                                                : hist.aprobacion === false
                                                  ? "pi pi-times"
                                                  : "pi pi-pencil"
                                            }
                                            css={{
                                              color:
                                                hist.aprobacion === true
                                                  ? "var(--verde)"
                                                  : hist.aprobacion === false
                                                    ? "var(--rojo)"
                                                    : "#9e9e9e",
                                              marginTop: "0.15em",
                                              fontSize: "0.85em"
                                            }}
                                          ></i>
                                          <div css={{ flex: 1, fontSize: "0.85em" }}>
                                            <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                              <small css={{ fontWeight: "600" }}>
                                                {hist.colaborador || "Sistema"}
                                              </small>
                                              <small css={{ color: "#999" }}>
                                                {hist.fecha && formatFecha(hist.fecha)}
                                              </small>
                                            </div>
                                            {hist.valor && (
                                              <div css={{ marginTop: "0.15em" }}>{hist.valor}</div>
                                            )}
                                            {hist.comentario && (
                                              <small css={{ display: "block", marginTop: "0.15em", fontStyle: "italic" }}>
                                                "{hist.comentario}"
                                              </small>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Verificaciones */}
                              {campo.ComentarioCampo?.length > 0 && (
                                <div css={{ marginTop: "0.5em" }}>
                                  <small css={{ color: "var(--grisTextos)", fontWeight: "600", display: "block", marginBottom: "0.5em" }}>
                                    <i className="pi pi-verified" css={{ marginRight: "0.5em" }}></i>
                                    Verificaciones
                                  </small>
                                  <div css={{ display: "flex", flexDirection: "column", gap: "0.4em" }}>
                                    {campo.ComentarioCampo.map((verificacion) => {
                                      const esDeEstePaso = verificacion.actividadPaso?.id === pasoSeleccionado.id;
                                      const nombreArchivo = verificacion.archivo?.nombredescarga || "";
                                      const esImagen = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(nombreArchivo);

                                      // Cargar imagen automáticamente si tiene archivo y es imagen
                                      if (verificacion.archivoId && esImagen && !imagenesVerificaciones[verificacion.id]) {
                                        cargarImagenVerificacion(verificacion.id);
                                      }

                                      return (
                                        <div
                                          key={verificacion.id}
                                          css={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "0.5em",
                                            padding: "0.4em 0.5em",
                                            backgroundColor: esDeEstePaso
                                              ? verificacion.aprobado
                                                ? "rgba(76, 175, 80, 0.15)"
                                                : "rgba(244, 67, 54, 0.15)"
                                              : "var(--surface-100)",
                                            borderRadius: "4px",
                                            borderLeft: `3px solid ${
                                              verificacion.aprobado
                                                ? "var(--verde)"
                                                : "var(--rojo)"
                                            }`,
                                            opacity: esDeEstePaso ? 1 : 0.7
                                          }}
                                        >
                                          <i
                                            className={verificacion.aprobado ? "pi pi-check" : "pi pi-times"}
                                            css={{
                                              color: verificacion.aprobado ? "var(--verde)" : "var(--rojo)",
                                              marginTop: "0.15em",
                                              fontSize: "0.85em"
                                            }}
                                          ></i>
                                          <div css={{ flex: 1, fontSize: "0.85em" }}>
                                            <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                              <small css={{ fontWeight: "600" }}>
                                                {verificacion.auditor?.nombre} {verificacion.auditor?.apellido}
                                              </small>
                                              <small css={{ color: "#999" }}>
                                                {formatFecha(verificacion.fecha)}
                                              </small>
                                            </div>
                                            {verificacion.actividadPaso?.nombre && (
                                              <small css={{ color: "var(--grisTextos)", display: "block" }}>
                                                {verificacion.actividadPaso.nombre}
                                                {verificacion.actividadPaso.pasoTipo?.nombre &&
                                                  ` (${verificacion.actividadPaso.pasoTipo.nombre})`}
                                              </small>
                                            )}
                                            {verificacion.comentario && (
                                              <small css={{ display: "block", marginTop: "0.15em", fontStyle: "italic" }}>
                                                "{verificacion.comentario}"
                                              </small>
                                            )}
                                            {verificacion.archivoId && esImagen && (
                                              <div css={{ marginTop: "0.4em" }}>
                                                {imagenesVerificaciones[verificacion.id] &&
                                                imagenesVerificaciones[verificacion.id] !== "error" ? (
                                                  <Image
                                                    src={imagenesVerificaciones[verificacion.id]}
                                                    alt="Imagen de verificación"
                                                    width="150"
                                                    preview
                                                    imageStyle={{ borderRadius: "4px" }}
                                                  />
                                                ) : imagenesVerificaciones[verificacion.id] === "error" ? (
                                                  <small css={{ color: "var(--rojo)" }}>
                                                    Error al cargar imagen
                                                  </small>
                                                ) : (
                                                  <div
                                                    css={{
                                                      width: "150px",
                                                      height: "100px",
                                                      backgroundColor: "#e0e0e0",
                                                      borderRadius: "4px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center"
                                                    }}
                                                  >
                                                    <i className="pi pi-spin pi-spinner"></i>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {verificacion.archivoId && !esImagen && (
                                              <div css={{ marginTop: "0.4em" }}>
                                                <Boton
                                                  texto
                                                  callback={() => {
                                                    downloadFile(
                                                      `admin/actividad/comentariocampo/${verificacion.id}/archivo`,
                                                      nombreArchivo,
                                                      () => {},
                                                      (error) => {
                                                        toast.current?.show({
                                                          severity: "error",
                                                          summary: "Error",
                                                          detail: error[0]?.msg || "Error al descargar",
                                                          sticky: true
                                                        });
                                                      }
                                                    );
                                                  }}
                                                >
                                                  <i className="pi pi-download" css={{ marginRight: "0.3em" }}></i>
                                                  {nombreArchivo}
                                                </Boton>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                        </div>
                      ))}

                      {productos.length === 0 && (
                        <p css={{ color: "var(--grisTextos)", fontSize: "0.9rem" }}>
                          En este paso no se revisó ningún campo de producto.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Decisión administrativa - solo para paso actual */}
                {pasoSeleccionado.actual && (
                  <Accordion
                    transitionOptions={{ timeout: 150 }}
                    css={{
                      marginTop: "1rem",
                      ".p-accordion-header-link": {
                        backgroundColor: "white !important",
                        color: "var(--negro) !important"
                      }
                    }}
                  >
                    <AccordionTab header="Decisión administrativa">
                      <Formulario
                        key={formKeyDecisionAdmin}
                        config={formConfigDecisionAdmin}
                        loader={loaderDecisionAdmin}
                        formularioEnCuerpo
                      />
                    </AccordionTab>
                  </Accordion>
                )}
              </div>
            )}
          </Sidebar>

          {/* Sidebar de colaboradores de todos los pasos */}
          <Sidebar
            visible={sidebarColaboradores}
            onHide={() => setSidebarColaboradores(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Menu
              ref={menuColaboradorTodos}
              popup
              model={modeloMenuColaborador()}
            />
            {actividadSeleccionada && (
              <div css={{ marginBottom: "50px" }}>
                <Titulo>Colaboradores</Titulo>
                {[...(actividadSeleccionada.actividadpaso || [])]
                  .sort(
                    (a, b) =>
                      (a.orden || 0) -
                      (b.orden || 0)
                  )
                  .map((paso, indice) => (
                    <div key={paso.id}>
                      {indice > 0 && (
                        <Divider
                          css={{
                            "&.p-divider-horizontal::before": {
                              borderTopWidth: "2px",
                              borderTopColor: "var(--grisTextos)"
                            }
                          }}
                        />
                      )}
                      <Formulario
                        key={`colab-${paso.id}-${formKeyColaborador}`}
                        config={generarFormConfigColaborador(paso)}
                        loader={loaderColaborador === paso.id}
                        formularioEnCuerpo
                      />
                      <div css={{ marginTop: "1em" }}>
                        {listaColaboradores(paso, menuColaboradorTodos)}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Sidebar>

          {/* Menú contextual hallazgos */}
          <Menu
            model={menuHallazgoItems}
            popup
            ref={menuHallazgoRef}
            popupAlignment="right"
          />

          {/* Sidebar detalle hallazgo */}
          <Sidebar
            visible={sidebarHallazgoDetalle}
            onHide={() => setSidebarHallazgoDetalle(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            {hallazgoSeleccionado && (
              <div css={{ marginBottom: "50px" }}>
                <Titulo>Detalles del hallazgo</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Tipo">
                    {hallazgoSeleccionado.hallazgoTipo?.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Resumen">
                    {hallazgoSeleccionado.resumen}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Texto" anchoCompleto>
                    {hallazgoSeleccionado.texto}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Auditor creador">
                    {hallazgoSeleccionado.auditorCreador?.nombre}{" "}
                    {hallazgoSeleccionado.auditorCreador?.apellido}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de creación">
                    {hallazgoSeleccionado.fechaCreacion &&
                      formatFecha(hallazgoSeleccionado.fechaCreacion)}
                  </CampoInformacion>
                  {hallazgoSeleccionado.fechaCierre && (
                    <CampoInformacion nombreCampo="Fecha de cierre">
                      {formatFecha(hallazgoSeleccionado.fechaCierre)}
                    </CampoInformacion>
                  )}
                </div>
                {hallazgoSeleccionado.interacciones?.length > 0 && (
                  <>
                    <Divider />
                    <Titulo>Interacciones</Titulo>
                    {renderInteracciones(hallazgoSeleccionado, "admin/hallazgo")}
                  </>
                )}
              </div>
            )}
          </Sidebar>

          {/* Sidebar editar hallazgo */}
          <Sidebar
            visible={sidebarHallazgoEditar}
            onHide={() => setSidebarHallazgoEditar(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            {hallazgoSeleccionado && (
              <div css={{ marginBottom: "50px" }}>
                <Titulo>Editar hallazgo</Titulo>
                <Formulario
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "hallazgoTipoId",
                            etiqueta: "Tipo de hallazgo",
                            tipoitemformularioid: 5,
                            opciones: hallazgoTipos,
                            requerido: true,
                            respuesta: hallazgoSeleccionado.hallazgoTipo?.nombre
                              ? hallazgoTipos.find(
                                  (t) =>
                                    t.etiqueta ===
                                    hallazgoSeleccionado.hallazgoTipo.nombre
                                )?.value
                              : null
                          },
                          {
                            nombre: "resumen",
                            etiqueta: "Resumen",
                            tipoitemformularioid: 1,
                            requerido: true,
                            notas: "No más de 3 o 4 palabras",
                            respuesta: hallazgoSeleccionado.resumen
                          },
                          {
                            nombre: "texto",
                            etiqueta: "Texto",
                            tipoitemformularioid: 11,
                            requerido: true,
                            respuesta: hallazgoSeleccionado.texto
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Guardar",
                        callback: editarHallazgo,
                        validar: true
                      }
                    ]
                  }}
                  loader={loaderHallazgoEditar}
                />
              </div>
            )}
          </Sidebar>

          {/* Sidebar interacción hallazgo */}
          <Sidebar
            visible={sidebarHallazgoInteraccion}
            onHide={() => setSidebarHallazgoInteraccion(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            {hallazgoSeleccionado && (
              <div css={{ marginBottom: "50px" }}>
                <Titulo>Interacción</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Tipo">
                    {hallazgoSeleccionado.hallazgoTipo?.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Resumen">
                    {hallazgoSeleccionado.resumen}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Texto" anchoCompleto>
                    {hallazgoSeleccionado.texto}
                  </CampoInformacion>
                </div>
                <Divider />
                {hallazgoSeleccionado.interacciones?.length > 0 && (
                  <div css={{ marginBottom: "1em" }}>
                    {renderInteracciones(hallazgoSeleccionado, "admin/hallazgo")}
                  </div>
                )}
                <Divider />
                <Formulario
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "contenido",
                            etiqueta: "Comentario",
                            tipoitemformularioid: 11,
                            requerido: true
                          },
                          {
                            nombre: "evidencia",
                            etiqueta: "Evidencia",
                            tipoitemformularioid: 10
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Enviar comentario",
                        callback: enviarInteraccionAdmin,
                        transparente: true,
                        validar: true
                      },
                      {
                        etiqueta: "Cerrar hallazgo",
                        callback: cerrarHallazgoAdmin,
                        confirmacion:
                          "Esta acción cerrará el hallazgo, ¿seguro?",
                        rojo: true,
                        validar: true
                      }
                    ]
                  }}
                  loader={loaderHallazgoInteraccion}
                />
              </div>
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
